import { api } from '../../lib/api.js';
import { authState } from '../../lib/auth.js';
import { BaseComponent } from '../../lib/base-component.js';
import { isFirstVersionOfKey } from '../../lib/consents.js';
import { providerLabel } from '../../lib/providers.js';
import {
    isConfidentialAppType,
    revealedSecretFor,
    revealFromResponse,
} from '../../lib/secret-reveal.js';
import { css, html } from '../../vendor/lit.min.js';

const NAV_GROUPS = [
    {
        label: 'Platform',
        icon: 'bi-building',
        items: [
            { id: 'tenants', label: 'Tenants' },
            { id: 'platform-events', label: 'Event Log' },
        ],
        staffOnly: true,
    },
    {
        label: 'Applications',
        icon: 'bi-window-stack',
        items: [
            { id: 'applications', label: 'Applications' },
            { id: 'apis', label: 'APIs' },
        ],
    },
    {
        label: 'User Management',
        icon: 'bi-people',
        items: [
            { id: 'users', label: 'Users' },
            { id: 'roles', label: 'Roles' },
        ],
    },
    {
        label: 'Settings',
        icon: 'bi-gear',
        items: [
            { id: 'settings', label: 'Invitations' },
            { id: 'documents', label: 'Documents' },
        ],
    },
    {
        label: 'Monitoring',
        icon: 'bi-activity',
        items: [{ id: 'events', label: 'Event Log' }],
    },
];

class AdminView extends BaseComponent {
    static properties = {
        activeTab: { type: String, state: true },
        _detailView: { type: Object, state: true },
        users: { type: Array, state: true },
        apis: { type: Array, state: true },
        applications: { type: Array, state: true },
        roles: { type: Array, state: true },
        _userDetail: { type: Object, state: true },
        _userDetailTab: { type: String, state: true },
        _userDetailRoles: { type: Array, state: true },
        _userDetailPermissions: { type: Array, state: true },
        _userDetailConsents: { type: Object, state: true },
        _userDetailIdentities: { type: Array, state: true },
        _userDetailForm: { type: Object, state: true },
        _userDetailDirty: { type: Boolean, state: true },
        _apiDetail: { type: Object, state: true },
        _apiDetailTab: { type: String, state: true },
        _apiDetailPermissions: { type: Array, state: true },
        _apiDetailForm: { type: Object, state: true },
        _apiDetailDirty: { type: Boolean, state: true },
        _roleDetail: { type: Object, state: true },
        _roleDetailTab: { type: String, state: true },
        _roleDetailPermissions: { type: Array, state: true },
        _roleDetailForm: { type: Object, state: true },
        _roleDetailDirty: { type: Boolean, state: true },
        _roleForm: { type: Object, state: true },
        allowedEmails: { type: Array, state: true },
        newEmail: { type: String, state: true },
        _invitations: { type: Array, state: true },
        _documents: { type: Array, state: true },
        _documentRequirements: { type: Array, state: true },
        _showDocumentForm: { type: Boolean, state: true },
        _documentForm: { type: Object, state: true },
        _requirementFor: { type: String, state: true },
        _showInviteForm: { type: Boolean, state: true },
        _inviteForm: { type: Object, state: true },
        error: { type: String, state: true },
        _confirmMessage: { type: String, state: true },
        _confirmLabel: { type: String, state: true },
        _revealedSecret: { type: Object, state: true },
        _secretCopied: { type: Boolean, state: true },
        _confirmCallback: { type: Object, state: true },
        _showAPIForm: { type: Boolean, state: true },
        _apiForm: { type: Object, state: true },
        _appDetail: { type: Object, state: true },
        _appDetailTab: { type: String, state: true },
        _appDetailForm: { type: Object, state: true },
        _appDetailDirty: { type: Boolean, state: true },
        _appDetailAPIs: { type: Array, state: true },
        _showAppForm: { type: Boolean, state: true },
        _appForm: { type: Object, state: true },
        _userForm: { type: Object, state: true },
        _tenants: { type: Array, state: true },
        _showTenantForm: { type: Boolean, state: true },
        _tenantForm: { type: Object, state: true },
        _staffTenants: { type: Array, state: true },
        _tenantDetail: { type: Object, state: true },
        _tenantDetailForm: { type: Object, state: true },
        _tenantDetailDirty: { type: Boolean, state: true },
        _events: { type: Array, state: true },
        _eventsTotal: { type: Number, state: true },
        _eventsOffset: { type: Number, state: true },
        _expandedEventId: { type: String, state: true },
        _platformEvents: { type: Array, state: true },
        _platformEventsTotal: { type: Number, state: true },
        _platformEventsOffset: { type: Number, state: true },
    };

    static styles = css`
        :host {
            display: block;
            background: #fff;
            margin: -1rem -1rem 0;
        }
        .page-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 0.5rem;
        }
        .page-header h4 {
            margin: 0;
            font-size: 1.5rem;
            font-weight: 600;
        }
        .page-subtitle {
            color: #666;
            font-size: 0.9rem;
            margin-bottom: 1.5rem;
        }
        .list-item {
            display: flex;
            align-items: center;
            padding: 0.875rem 1rem;
            border-bottom: 1px solid #eee;
            cursor: pointer;
            gap: 1rem;
        }
        .list-item:hover { background: #f8f9fa; }
        .list-item:last-child { border-bottom: none; }
        .list-item-icon {
            width: 40px;
            height: 40px;
            border-radius: 8px;
            background: #f0f0f0;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.1rem;
            color: #666;
            flex-shrink: 0;
        }
        .list-item-info { flex: 1; min-width: 0; }
        .list-item-name { font-weight: 500; color: #0d6efd; font-size: 0.95rem; }
        .list-item-detail { font-size: 0.8rem; color: #888; margin-top: 1px; }
        .list-item-meta {
            font-size: 0.8rem;
            color: #888;
            white-space: nowrap;
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }
        .list-item-meta code { font-size: 0.8rem; color: #666; }
        .list-item-actions { display: flex; gap: 0.25rem; }
        .list-container { border: 1px solid #eee; border-radius: 8px; overflow: hidden; }
        .detail-panel { background: #f8f9fa; border-radius: 0.5rem; padding: 1.25rem; margin-top: 1.25rem; }
        .badge-list { display: flex; flex-wrap: wrap; gap: 0.25rem; }
        table { font-size: 0.9rem; }
        .form-panel {
            border: 1px solid #e5e5e5;
            border-radius: 8px;
            padding: 1.25rem;
            margin-bottom: 1.25rem;
            background: #fff;
        }
        .back-link {
            display: inline-flex;
            align-items: center;
            gap: 0.25rem;
            color: #666;
            text-decoration: none;
            font-size: 0.9rem;
            margin-bottom: 1rem;
        }
        .back-link:hover { color: #333; }
        .detail-header {
            display: flex;
            align-items: center;
            gap: 1rem;
            margin-bottom: 1.5rem;
        }
        .detail-header-icon {
            width: 56px;
            height: 56px;
            border-radius: 12px;
            background: #f0f0f0;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 1.5rem;
            color: #666;
            flex-shrink: 0;
        }
        .detail-header-info h4 {
            margin: 0;
            font-size: 1.5rem;
            font-weight: 600;
        }
        .detail-header-meta {
            font-size: 0.85rem;
            color: #888;
            display: flex;
            gap: 1rem;
            margin-top: 0.25rem;
        }
        .detail-header-meta code {
            background: #f0f0f0;
            padding: 0.1rem 0.4rem;
            border-radius: 3px;
            font-size: 0.8rem;
        }
        .settings-section {
            border: 1px solid #e5e5e5;
            border-radius: 8px;
            padding: 1.5rem;
            margin-bottom: 1rem;
        }
        .settings-section h5 {
            margin: 0 0 1.25rem;
            font-size: 1.1rem;
            font-weight: 600;
        }
    `;

    constructor() {
        super();
        this.activeTab = this._getTabFromPath();
        this._expandedGroups = new Set(
            NAV_GROUPS.filter((g) => g.items.some((i) => i.id === this.activeTab)).map(
                (g) => g.label,
            ),
        );
        this._detailView = null;
        this.users = [];
        this.apis = [];
        this.applications = [];
        this.roles = [];
        this._userDetail = null;
        this._userDetailTab = 'details';
        this._userDetailRoles = [];
        this._userDetailPermissions = [];
        this._userDetailConsents = { accepted: [], outstanding: [] };
        this._userDetailIdentities = [];
        this._userDetailForm = {};
        this._userDetailDirty = false;
        this._apiDetail = null;
        this._apiDetailTab = 'settings';
        this._apiDetailPermissions = [];
        this._apiDetailForm = {};
        this._apiDetailDirty = false;
        this._roleDetail = null;
        this._roleDetailTab = 'settings';
        this._roleDetailPermissions = [];
        this._roleDetailForm = {};
        this._roleDetailDirty = false;
        this._roleForm = { name: '', description: '' };
        this.allowedEmails = [];
        this.newEmail = '';
        this._invitations = [];
        this._documents = [];
        this._documentRequirements = [];
        this._showDocumentForm = false;
        this._documentForm = { key: '', title: '', body: '', requires_reconsent: true };
        this._requirementFor = '';
        this._showInviteForm = false;
        this._inviteForm = {
            email: '',
            client_id: '',
            redirect_uri: '',
            role_ids: [],
            message: '',
        };
        this.error = '';
        this._confirmMessage = '';
        this._confirmLabel = '';
        this._confirmCallback = null;
        // A client secret shown once, after create or rotate (#147). Held only
        // here, never in storage, so a reload or a later visit never shows it.
        this._revealedSecret = null;
        this._secretCopied = false;
        this._showAPIForm = false;
        this._apiForm = {
            name: '',
            identifier: '',
            token_lifetime: 900,
            allow_offline_access: false,
            allow_self_registered_clients: false,
        };
        this._appDetail = null;
        this._appDetailTab = 'settings';
        this._appDetailForm = {};
        this._appDetailDirty = false;
        this._appDetailAPIs = [];
        this._showAppForm = false;
        this._appForm = {
            name: '',
            client_id: '',
            app_type: 'regular_web',
            redirect_uris: '',
            web_origins: '',
            description: '',
        };
        this._userForm = { email: '', username: '', first_name: '', last_name: '' };
        this._tenants = [];
        this._showTenantForm = false;
        this._tenantForm = { name: '', slug: '' };
        this._staffTenants = [];
        this._tenantDetail = null;
        this._tenantDetailForm = {};
        this._tenantDetailDirty = false;
    }

    _getTabFromPath() {
        const path = window.location.pathname;
        const allIds = NAV_GROUPS.flatMap((g) => g.items.map((i) => i.id));
        const match = path.match(/^\/admin\/([^/]+)/);
        if (match && allIds.includes(match[1])) {
            return match[1];
        }
        return 'applications';
    }

    _getDetailFromPath() {
        const path = window.location.pathname;
        if (path === '/admin/users/new') return { type: 'user-new' };
        const userMatch = path.match(/^\/admin\/users\/([^/]+)/);
        if (userMatch) return { type: 'user', id: userMatch[1] };
        if (path === '/admin/apis/new') return { type: 'api-new' };
        const apiMatch = path.match(/^\/admin\/apis\/([^/]+)/);
        if (apiMatch) return { type: 'api', id: apiMatch[1] };
        if (path === '/admin/roles/new') return { type: 'role-new' };
        const roleMatch = path.match(/^\/admin\/roles\/([^/]+)/);
        if (roleMatch) return { type: 'role', id: roleMatch[1] };
        if (path === '/admin/applications/new') return { type: 'app-new' };
        const appMatch = path.match(/^\/admin\/applications\/([^/]+)/);
        if (appMatch) return { type: 'app', id: appMatch[1] };
        return null;
    }

    async connectedCallback() {
        super.connectedCallback();
        this._onPopState = () => {
            this._revealedSecret = null;
            this.activeTab = this._getTabFromPath();
            const detail = this._getDetailFromPath();
            if (detail) {
                this._navigateToDetail(detail, false);
            } else {
                this._detailView = null;
                this._apiDetail = null;
                this._appDetail = null;
                this._userDetail = null;
                this._roleDetail = null;
                this._loadTab();
            }
        };
        window.addEventListener('popstate', this._onPopState);

        const detail = this._getDetailFromPath();
        if (detail) {
            this._navigateToDetail(detail, false);
        } else {
            this._loadTab();
        }

        // Reload current tab when tenant context changes
        this._unsubscribeAuth = authState.subscribe((state) => {
            if (state.staffTenantId !== this._lastStaffTenantId) {
                this._lastStaffTenantId = state.staffTenantId;
                this._revealedSecret = null;
                this._detailView = null;
                this._apiDetail = null;
                this._appDetail = null;
                this._userDetail = null;
                this._roleDetail = null;
                this._tenantDetail = null;
                this._loadTab();
            }
        });
        this._lastStaffTenantId = authState.staffTenantId;
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener('popstate', this._onPopState);
        this._unsubscribeAuth?.();
    }

    async _navigateToDetail(detail, pushState = true) {
        this._detailView = detail;
        this.error = '';

        if (detail.type === 'user') {
            this.activeTab = 'users';
            const group = NAV_GROUPS.find((g) => g.items.some((i) => i.id === 'users'));
            if (group) this._expandedGroups.add(group.label);

            if (pushState) window.history.pushState({}, '', `/admin/users/${detail.id}`);

            try {
                const user = await api.get(`/api/admin/users/${detail.id}`);
                this._userDetail = user;
                this._userDetailTab = 'details';
                this._userDetailForm = {
                    email: user.email,
                    username: user.username || '',
                    first_name: user.first_name || '',
                    last_name: user.last_name || '',
                    email_verified: user.email_verified,
                };
                this._userDetailDirty = false;

                const [rolesData, permsData, consentsData, identitiesData] = await Promise.all([
                    api.get(`/api/admin/users/${detail.id}/roles`),
                    api.get(`/api/admin/users/${detail.id}/permissions`),
                    api.get(`/api/admin/users/${detail.id}/consents`),
                    api.get(`/api/admin/users/${detail.id}/identities`),
                ]);
                this._userDetailRoles = rolesData.roles || [];
                this._userDetailPermissions = permsData.permissions || [];
                this._userDetailConsents = {
                    accepted: consentsData.accepted || [],
                    outstanding: consentsData.outstanding || [],
                };
                this._userDetailIdentities = identitiesData.identities || [];
            } catch (err) {
                this.error = err.message;
            }
        }

        if (detail.type === 'api') {
            this.activeTab = 'apis';
            const group = NAV_GROUPS.find((g) => g.items.some((i) => i.id === 'apis'));
            if (group) this._expandedGroups.add(group.label);

            if (pushState) {
                window.history.pushState({}, '', `/admin/apis/${detail.id}`);
            }

            try {
                // Load APIs list if not loaded, then find the one we need
                if (!this.apis.length) {
                    const data = await api.get('/api/admin/apis');
                    this.apis = data.apis || [];
                }
                const apiItem = this.apis.find((a) => a.id === detail.id);
                if (!apiItem) {
                    this.error = 'API not found';
                    return;
                }
                this._apiDetail = apiItem;
                this._apiDetailForm = {
                    name: apiItem.name,
                    token_lifetime: apiItem.token_lifetime,
                    allow_offline_access: apiItem.allow_offline_access,
                    allow_self_registered_clients: apiItem.allow_self_registered_clients,
                };
                this._apiDetailDirty = false;
                this._apiDetailTab = 'settings';

                const permData = await api.get(`/api/admin/apis/${detail.id}/permissions`);
                this._apiDetailPermissions = permData.permissions || [];
            } catch (err) {
                this.error = err.message;
            }
        }

        if (detail.type === 'app') {
            this.activeTab = 'applications';
            const group = NAV_GROUPS.find((g) => g.items.some((i) => i.id === 'applications'));
            if (group) this._expandedGroups.add(group.label);

            if (pushState) {
                window.history.pushState({}, '', `/admin/applications/${detail.id}`);
            }

            try {
                const appItem = await api.get(`/api/admin/applications/${detail.id}`);
                this._appDetail = appItem;
                this._appDetailForm = {
                    name: appItem.name,
                    app_type: appItem.app_type,
                    redirect_uris: (appItem.redirect_uris || []).join('\n'),
                    allowed_logout_urls: (appItem.allowed_logout_urls || []).join('\n'),
                    web_origins: (appItem.web_origins || []).join('\n'),
                    description: appItem.description || '',
                    refresh_token_lifetime: appItem.refresh_token_lifetime,
                    refresh_token_idle_lifetime: appItem.refresh_token_idle_lifetime,
                    linked_application_ids: appItem.linked_application_ids || [],
                };
                this._appDetailDirty = false;
                this._appDetailTab = 'settings';

                const apisData = await api.get(`/api/admin/applications/${detail.id}/apis`);
                this._appDetailAPIs = apisData.apis || [];
            } catch (err) {
                this.error = err.message;
            }
        }

        if (detail.type === 'role') {
            this.activeTab = 'roles';
            const group = NAV_GROUPS.find((g) => g.items.some((i) => i.id === 'roles'));
            if (group) this._expandedGroups.add(group.label);
            if (pushState) window.history.pushState({}, '', `/admin/roles/${detail.id}`);

            try {
                const role = await api.get(`/api/admin/roles/${detail.id}`);
                this._roleDetail = role;
                this._roleDetailForm = { name: role.name, description: role.description || '' };
                this._roleDetailDirty = false;
                this._roleDetailTab = 'settings';

                const permData = await api.get(`/api/admin/roles/${detail.id}/permissions`);
                this._roleDetailPermissions = permData.permissions || [];
            } catch (err) {
                this.error = err.message;
            }
        }

        if (detail.type === 'role-new') {
            this.activeTab = 'roles';
            const group = NAV_GROUPS.find((g) => g.items.some((i) => i.id === 'roles'));
            if (group) this._expandedGroups.add(group.label);
            this._roleForm = { name: '', description: '' };
            if (pushState) window.history.pushState({}, '', '/admin/roles/new');
        }

        if (detail.type === 'user-new') {
            this.activeTab = 'users';
            const group = NAV_GROUPS.find((g) => g.items.some((i) => i.id === 'users'));
            if (group) this._expandedGroups.add(group.label);
            this._userForm = { email: '', username: '', first_name: '', last_name: '' };
            if (pushState) window.history.pushState({}, '', '/admin/users/new');
        }

        if (detail.type === 'api-new') {
            this.activeTab = 'apis';
            const group = NAV_GROUPS.find((g) => g.items.some((i) => i.id === 'apis'));
            if (group) this._expandedGroups.add(group.label);
            this._apiForm = {
                name: '',
                identifier: '',
                token_lifetime: 900,
                allow_offline_access: false,
                allow_self_registered_clients: false,
            };
            if (pushState) window.history.pushState({}, '', '/admin/apis/new');
        }

        if (detail.type === 'app-new') {
            this.activeTab = 'applications';
            const group = NAV_GROUPS.find((g) => g.items.some((i) => i.id === 'applications'));
            if (group) this._expandedGroups.add(group.label);
            this._appForm = {
                name: '',
                client_id: '',
                app_type: 'regular_web',
                redirect_uris: '',
                web_origins: '',
                description: '',
            };
            if (pushState) window.history.pushState({}, '', '/admin/applications/new');
        }
    }

    _backToList() {
        this._revealedSecret = null;
        this._detailView = null;
        this._apiDetail = null;
        this._appDetail = null;
        this._userDetail = null;
        this._roleDetail = null;
        window.history.pushState({}, '', `/admin/${this.activeTab}`);
        this._loadTab();
    }

    async _loadTab() {
        this.error = '';
        try {
            if (this.activeTab === 'users') {
                const data = await api.get('/api/admin/users');
                this.users = data.users || [];
            } else if (this.activeTab === 'apis') {
                const data = await api.get('/api/admin/apis');
                this.apis = data.apis || [];
            } else if (this.activeTab === 'applications') {
                const data = await api.get('/api/admin/applications');
                this.applications = data.applications || [];
            } else if (this.activeTab === 'roles') {
                const data = await api.get('/api/admin/roles');
                this.roles = data.roles || [];
            } else if (this.activeTab === 'documents') {
                const [docData, reqData] = await Promise.all([
                    api.get('/api/admin/documents'),
                    api.get('/api/admin/document-requirements'),
                ]);
                this._documents = docData.documents || [];
                this._documentRequirements = reqData.requirements || [];
                if (!this.apis.length) {
                    const apiData = await api.get('/api/admin/apis');
                    this.apis = apiData.apis || [];
                }
            } else if (this.activeTab === 'settings') {
                const invData = await api.get('/api/admin/invitations');
                this._invitations = invData.invitations || [];
                if (!this.roles.length) {
                    const rolesData = await api.get('/api/admin/roles');
                    this.roles = rolesData.roles || [];
                }
                // The invite form picks the app from these rather than taking a
                // typed name: an invitation now records which registered client
                // raised it (#101), and free text cannot be resolved to one.
                if (!this.applications.length) {
                    const appData = await api.get('/api/admin/applications');
                    this.applications = appData.applications || [];
                }
            } else if (this.activeTab === 'tenants') {
                const data = await api.get('/api/staff/tenants');
                this._tenants = data.tenants || [];
            } else if (this.activeTab === 'events') {
                this._eventsOffset = 0;
                await this._loadEvents();
            } else if (this.activeTab === 'platform-events') {
                this._platformEventsOffset = 0;
                await this._loadPlatformEvents();
            }
        } catch (err) {
            this.error = err.message;
        }
    }

    _toggleGroup(label) {
        if (this._expandedGroups.has(label)) {
            this._expandedGroups.delete(label);
        } else {
            this._expandedGroups.add(label);
        }
        this.requestUpdate();
    }

    _switchTab(tab) {
        this.activeTab = tab;
        this._revealedSecret = null;
        this._detailView = null;
        this._apiDetail = null;
        this._appDetail = null;
        this._userDetail = null;
        this._roleDetail = null;
        this._showAPIForm = false;
        const group = NAV_GROUPS.find((g) => g.items.some((i) => i.id === tab));
        if (group) this._expandedGroups.add(group.label);
        window.history.pushState({}, '', `/admin/${tab}`);
        this._loadTab();
    }

    // ========== User methods ==========

    _showNewUserForm() {
        this._navigateToDetail({ type: 'user-new' });
    }

    async _handleUserFormSubmit(e) {
        e.preventDefault();
        try {
            const created = await api.post('/api/admin/users', this._userForm);
            this._navigateToDetail({ type: 'user', id: created.id });
        } catch (err) {
            this.error = err.message;
        }
    }

    _updateUserDetailForm(field, value) {
        this._userDetailForm = { ...this._userDetailForm, [field]: value };
        this._userDetailDirty = true;
    }

    _resetUserDetailForm() {
        const u = this._userDetail;
        this._userDetailForm = {
            email: u.email,
            username: u.username || '',
            first_name: u.first_name || '',
            last_name: u.last_name || '',
            email_verified: u.email_verified,
        };
        this._userDetailDirty = false;
    }

    async _saveUserDetail() {
        if (!this._userDetail) return;
        try {
            const updated = await api.put(`/api/admin/users/${this._userDetail.id}`, {
                email: this._userDetailForm.email,
                username: this._userDetailForm.username,
                first_name: this._userDetailForm.first_name || null,
                last_name: this._userDetailForm.last_name || null,
                email_verified: this._userDetailForm.email_verified,
            });
            this._userDetail = updated;
            this._userDetailDirty = false;
        } catch (err) {
            this.error = err.message;
        }
    }

    _openUserDetail(user) {
        this._navigateToDetail({ type: 'user', id: user.id });
    }

    _deleteUser(user) {
        this._confirmMessage = `Are you sure you want to delete ${user.email}?`;
        this._confirmCallback = async () => {
            try {
                await api.delete(`/api/admin/users/${user.id}`);
                this._userDetail = null;
                this._detailView = null;
                window.history.pushState({}, '', '/admin/users');
                this._loadTab();
            } catch (err) {
                this.error = err.message;
            }
        };
    }

    async _addUserRole(roleId) {
        try {
            await api.post(`/api/admin/users/${this._userDetail.id}/roles`, { role_id: roleId });
            const data = await api.get(`/api/admin/users/${this._userDetail.id}/roles`);
            this._userDetailRoles = data.roles || [];
        } catch (err) {
            this.error = err.message;
        }
    }

    async _removeUserRole(roleId) {
        try {
            await api.delete(`/api/admin/users/${this._userDetail.id}/roles/${roleId}`);
            const data = await api.get(`/api/admin/users/${this._userDetail.id}/roles`);
            this._userDetailRoles = data.roles || [];
        } catch (err) {
            this.error = err.message;
        }
    }

    // ========== API methods ==========

    _isManagementAPI(apiItem) {
        return apiItem.identifier === 'https://api.auth.dicedriven.com';
    }

    _openAPIDetail(apiItem) {
        this._navigateToDetail({ type: 'api', id: apiItem.id });
    }

    _showNewAPIForm() {
        this._navigateToDetail({ type: 'api-new' });
    }

    async _handleAPIFormSubmit(e) {
        e.preventDefault();
        try {
            const created = await api.post('/api/admin/apis', {
                name: this._apiForm.name,
                identifier: this._apiForm.identifier,
                token_lifetime: parseInt(String(this._apiForm.token_lifetime), 10),
                allow_offline_access: this._apiForm.allow_offline_access,
                allow_self_registered_clients: this._apiForm.allow_self_registered_clients,
            });
            this.apis = [];
            this._navigateToDetail({ type: 'api', id: created.id });
        } catch (err) {
            this.error = err.message;
        }
    }

    async _saveAPIDetail() {
        if (!this._apiDetail) return;
        try {
            const updated = await api.put(`/api/admin/apis/${this._apiDetail.id}`, {
                name: this._apiDetailForm.name,
                token_lifetime: parseInt(this._apiDetailForm.token_lifetime, 10),
                allow_offline_access: this._apiDetailForm.allow_offline_access,
                allow_self_registered_clients: this._apiDetailForm.allow_self_registered_clients,
            });
            this._apiDetail = updated;
            this._apiDetailDirty = false;
            // Refresh list
            const data = await api.get('/api/admin/apis');
            this.apis = data.apis || [];
        } catch (err) {
            this.error = err.message;
        }
    }

    _deleteAPI(apiItem) {
        this._confirmMessage = `Are you sure you want to delete the API "${apiItem.name}"?`;
        this._confirmCallback = async () => {
            try {
                await api.delete(`/api/admin/apis/${apiItem.id}`);
                this._apiDetail = null;
                this._detailView = null;
                window.history.pushState({}, '', '/admin/apis');
                this._loadTab();
            } catch (err) {
                this.error = err.message;
            }
        };
    }

    _updateAPIDetailForm(field, value) {
        this._apiDetailForm = { ...this._apiDetailForm, [field]: value };
        this._apiDetailDirty = true;
    }

    // ========== Application methods ==========

    _openAppDetail(appItem) {
        this._navigateToDetail({ type: 'app', id: appItem.id });
    }

    _showNewAppForm() {
        this._navigateToDetail({ type: 'app-new' });
    }

    async _handleAppFormSubmit(e) {
        e.preventDefault();
        try {
            const redirectURIs = this._appForm.redirect_uris
                .split('\n')
                .map((s) => s.trim())
                .filter(Boolean);
            const webOrigins = this._appForm.web_origins
                .split('\n')
                .map((s) => s.trim())
                .filter(Boolean);
            const created = await api.post('/api/admin/applications', {
                name: this._appForm.name,
                client_id: this._appForm.client_id,
                app_type: this._appForm.app_type,
                redirect_uris: redirectURIs,
                web_origins: webOrigins,
                description: this._appForm.description || null,
            });
            this._showAppForm = false;
            await this._navigateToDetail({ type: 'app', id: created.id });
            // The create response is the only time the secret exists (#147).
            this._revealedSecret = revealFromResponse(created, created);
            this._secretCopied = false;
        } catch (err) {
            this.error = err.message;
        }
    }

    _updateAppDetailForm(field, value) {
        this._appDetailForm = { ...this._appDetailForm, [field]: value };
        this._appDetailDirty = true;
    }

    _toggleLinkedApplication(id, linked) {
        const current = this._appDetailForm.linked_application_ids || [];
        const next = linked ? [...current, id] : current.filter((existing) => existing !== id);
        this._updateAppDetailForm('linked_application_ids', next);
    }

    async _saveAppDetail() {
        if (!this._appDetail) return;
        try {
            const redirectURIs = this._appDetailForm.redirect_uris
                .split('\n')
                .map((s) => s.trim())
                .filter(Boolean);
            const allowedLogoutURLs = this._appDetailForm.allowed_logout_urls
                .split('\n')
                .map((s) => s.trim())
                .filter(Boolean);
            const webOrigins = this._appDetailForm.web_origins
                .split('\n')
                .map((s) => s.trim())
                .filter(Boolean);
            const updated = await api.put(`/api/admin/applications/${this._appDetail.id}`, {
                name: this._appDetailForm.name,
                app_type: this._appDetailForm.app_type,
                redirect_uris: redirectURIs,
                allowed_logout_urls: allowedLogoutURLs,
                web_origins: webOrigins,
                description: this._appDetailForm.description || null,
                // Only an M2M application has links. Changing the type hides the
                // section, so send none rather than links nothing can uncheck.
                linked_application_ids:
                    this._appDetailForm.app_type === 'machine_to_machine'
                        ? this._appDetailForm.linked_application_ids || []
                        : [],
                refresh_token_lifetime: parseInt(this._appDetailForm.refresh_token_lifetime, 10),
                refresh_token_idle_lifetime: parseInt(
                    this._appDetailForm.refresh_token_idle_lifetime,
                    10,
                ),
            });
            this._appDetail = updated;
            this._appDetailDirty = false;
        } catch (err) {
            this.error = err.message;
        }
    }

    _deleteApp(appItem) {
        this._confirmMessage = `Are you sure you want to delete the application "${appItem.name}"?`;
        this._confirmCallback = async () => {
            try {
                await api.delete(`/api/admin/applications/${appItem.id}`);
                this._appDetail = null;
                this._detailView = null;
                window.history.pushState({}, '', '/admin/applications');
                this._loadTab();
            } catch (err) {
                this.error = err.message;
            }
        };
    }

    _rotateAppSecret(appItem) {
        this._confirmLabel = 'Rotate secret';
        this._confirmMessage = `Rotate the client secret for "${appItem.name}"? The current secret stops working immediately, and every client using it will fail to authenticate until it is reconfigured with the new one.`;
        this._confirmCallback = async () => {
            try {
                const rotated = await api.post(
                    `/api/admin/applications/${appItem.id}/rotate-secret`,
                    {},
                );
                this._revealedSecret = revealFromResponse(rotated, appItem);
                this._secretCopied = false;
            } catch (err) {
                this.error = err.message;
            }
        };
    }

    async _copyRevealedSecret() {
        try {
            await navigator.clipboard.writeText(this._revealedSecret.secret);
            this._secretCopied = true;
            setTimeout(() => {
                this._secretCopied = false;
            }, 2000);
        } catch {
            this.error = 'Could not copy to the clipboard. Select the secret and copy it by hand.';
        }
    }

    _dismissRevealedSecret() {
        this._revealedSecret = null;
        this._secretCopied = false;
    }

    async _addAppAPIGrant(apiId) {
        try {
            await api.post(`/api/admin/applications/${this._appDetail.id}/apis`, { api_id: apiId });
            const apisData = await api.get(`/api/admin/applications/${this._appDetail.id}/apis`);
            this._appDetailAPIs = apisData.apis || [];
        } catch (err) {
            this.error = err.message;
        }
    }

    async _removeAppAPIGrant(apiId) {
        try {
            await api.delete(`/api/admin/applications/${this._appDetail.id}/apis/${apiId}`);
            const apisData = await api.get(`/api/admin/applications/${this._appDetail.id}/apis`);
            this._appDetailAPIs = apisData.apis || [];
        } catch (err) {
            this.error = err.message;
        }
    }

    // ========== Role methods ==========

    _openRoleDetail(role) {
        this._navigateToDetail({ type: 'role', id: role.id });
    }

    _showNewRoleForm() {
        this._navigateToDetail({ type: 'role-new' });
    }

    async _handleRoleFormSubmit(e) {
        e.preventDefault();
        try {
            const created = await api.post('/api/admin/roles', {
                name: this._roleForm.name,
                description: this._roleForm.description || null,
            });
            this._navigateToDetail({ type: 'role', id: created.id });
        } catch (err) {
            this.error = err.message;
        }
    }

    _updateRoleDetailForm(field, value) {
        this._roleDetailForm = { ...this._roleDetailForm, [field]: value };
        this._roleDetailDirty = true;
    }

    async _saveRoleDetail() {
        if (!this._roleDetail) return;
        try {
            const updated = await api.put(`/api/admin/roles/${this._roleDetail.id}`, {
                name: this._roleDetailForm.name,
                description: this._roleDetailForm.description || null,
            });
            this._roleDetail = updated;
            this._roleDetailDirty = false;
        } catch (err) {
            this.error = err.message;
        }
    }

    _deleteRole(role) {
        this._confirmMessage = `Are you sure you want to delete the role "${role.name}"?`;
        this._confirmCallback = async () => {
            try {
                await api.delete(`/api/admin/roles/${role.id}`);
                this._roleDetail = null;
                this._detailView = null;
                window.history.pushState({}, '', '/admin/roles');
                this._loadTab();
            } catch (err) {
                this.error = err.message;
            }
        };
    }

    async _addRolePermission(permissionId) {
        try {
            await api.post(`/api/admin/roles/${this._roleDetail.id}/permissions`, {
                permission_id: permissionId,
            });
            const data = await api.get(`/api/admin/roles/${this._roleDetail.id}/permissions`);
            this._roleDetailPermissions = data.permissions || [];
        } catch (err) {
            this.error = err.message;
        }
    }

    async _removeRolePermission(permissionId) {
        try {
            await api.delete(`/api/admin/roles/${this._roleDetail.id}/permissions/${permissionId}`);
            const data = await api.get(`/api/admin/roles/${this._roleDetail.id}/permissions`);
            this._roleDetailPermissions = data.permissions || [];
        } catch (err) {
            this.error = err.message;
        }
    }

    // ========== Settings methods ==========

    async _handleAddEmail(e) {
        e.preventDefault();
        if (!this.newEmail) return;
        try {
            await api.post('/api/admin/allowed-emails', { email: this.newEmail.toLowerCase() });
            this.newEmail = '';
            this._loadTab();
        } catch (err) {
            this.error = err.message;
        }
    }

    async _handleRemoveEmail(id) {
        try {
            await api.delete(`/api/admin/allowed-emails/${id}`);
            this._loadTab();
        } catch (err) {
            this.error = err.message;
        }
    }

    // ========== Confirm dialog ==========

    _confirmYes() {
        if (this._confirmCallback) this._confirmCallback();
        this._confirmMessage = '';
        this._confirmLabel = '';
        this._confirmCallback = null;
    }

    _confirmNo() {
        this._confirmMessage = '';
        this._confirmLabel = '';
        this._confirmCallback = null;
    }

    // ========== Render ==========

    render() {
        return html`
            <div class="d-flex" style="min-height: calc(100vh - 56px);">
                <nav class="border-end pt-3" style="width: 220px; flex-shrink: 0;">
                    ${NAV_GROUPS.filter(
                        (g) => !g.staffOnly || authState.hasPermission('manage:tenants'),
                    ).map((group) => {
                        const expanded = this._expandedGroups.has(group.label);
                        return html`
                            <div class="mb-1">
                                <div class="d-flex align-items-center gap-2 px-3 py-2 text-dark" style="font-size: 0.9rem; font-weight: 500; cursor: pointer;"
                                     @click="${() => this._toggleGroup(group.label)}">
                                    <i class="bi ${group.icon}" style="font-size: 1rem; color: #666;"></i>
                                    <span class="flex-grow-1">${group.label}</span>
                                    <i class="bi ${expanded ? 'bi-chevron-down' : 'bi-chevron-right'}" style="font-size: 0.7rem; color: #999;"></i>
                                </div>
                                ${
                                    expanded
                                        ? html`
                                    <div class="ms-4">
                                        ${group.items.map(
                                            (item) => html`
                                            <a class="d-block px-3 py-1 text-decoration-none ${this.activeTab === item.id ? 'text-primary fw-medium' : 'text-secondary'}"
                                               style="font-size: 0.85rem;"
                                               href="/admin/${item.id}"
                                               @click="${(e) => {
                                                   e.preventDefault();
                                                   this._switchTab(item.id);
                                               }}">
                                                ${item.label}
                                            </a>
                                        `,
                                        )}
                                    </div>
                                `
                                        : ''
                                }
                            </div>
                        `;
                    })}
                </nav>

                <main class="flex-grow-1 d-flex justify-content-center p-4">
                    <div style="width: 100%; max-width: 860px;">
                        ${this.error ? html`<div class="alert alert-danger">${this.error}</div>` : ''}

                        ${
                            this._detailView?.type === 'user-new'
                                ? this._renderUserCreate()
                                : this._detailView?.type === 'user' && this._userDetail
                                  ? this._renderUserDetail()
                                  : this._detailView?.type === 'api-new'
                                    ? this._renderAPICreate()
                                    : this._detailView?.type === 'api' && this._apiDetail
                                      ? this._renderAPIDetail()
                                      : this._detailView?.type === 'role-new'
                                        ? this._renderRoleCreate()
                                        : this._detailView?.type === 'role' && this._roleDetail
                                          ? this._renderRoleDetail()
                                          : this._detailView?.type === 'app-new'
                                            ? this._renderAppCreate()
                                            : this._detailView?.type === 'app' && this._appDetail
                                              ? this._renderAppDetail()
                                              : html`
                                ${this.activeTab === 'users' ? this._renderUsers() : ''}
                                ${this.activeTab === 'apis' ? this._renderAPIs() : ''}
                                ${this.activeTab === 'applications' ? this._renderApplications() : ''}
                                ${this.activeTab === 'roles' ? this._renderRoles() : ''}
                                ${this.activeTab === 'settings' ? this._renderSettings() : ''}
                                ${this.activeTab === 'documents' ? this._renderDocuments() : ''}
                                ${this.activeTab === 'tenants' ? this._renderTenants() : ''}
                                ${this.activeTab === 'platform-events' ? this._renderPlatformEventLog() : ''}
                                ${this.activeTab === 'events' ? this._renderEventLog() : ''}
                            `
                        }
                    </div>
                </main>
            </div>

            ${
                this._confirmMessage
                    ? html`
                <div class="modal show d-block" tabindex="-1" style="background: rgba(0,0,0,0.5);">
                    <div class="modal-dialog modal-dialog-centered">
                        <div class="modal-content">
                            <div class="modal-body"><p>${this._confirmMessage}</p></div>
                            <div class="modal-footer">
                                <button class="btn btn-secondary" @click="${this._confirmNo}">Cancel</button>
                                <button class="btn btn-danger" @click="${this._confirmYes}">${this._confirmLabel || 'Delete'}</button>
                            </div>
                        </div>
                    </div>
                </div>
            `
                    : ''
            }
        `;
    }

    // ========== List views ==========

    _renderUserCreate() {
        return html`
            <a class="back-link" href="/admin/users" @click="${(e) => {
                e.preventDefault();
                this._backToList();
            }}">
                <i class="bi bi-arrow-left"></i> Back to Users
            </a>

            <h4 style="font-weight: 600; margin-bottom: 1.5rem;">Create User</h4>

            <div class="settings-section">
                <form @submit="${this._handleUserFormSubmit}">
                    <div class="mb-3">
                        <label class="form-label">Email *</label>
                        <input type="email" class="form-control" required
                            .value="${this._userForm.email}"
                            @input="${(e) => (this._userForm = { ...this._userForm, email: e.target.value })}">
                    </div>
                    <div class="mb-3">
                        <label class="form-label">Username</label>
                        <input type="text" class="form-control"
                            .value="${this._userForm.username}"
                            @input="${(e) => (this._userForm = { ...this._userForm, username: e.target.value })}">
                    </div>
                    <div class="row">
                        <div class="col-md-6 mb-3">
                            <label class="form-label">First Name</label>
                            <input type="text" class="form-control"
                                .value="${this._userForm.first_name}"
                                @input="${(e) => (this._userForm = { ...this._userForm, first_name: e.target.value })}">
                        </div>
                        <div class="col-md-6 mb-3">
                            <label class="form-label">Last Name</label>
                            <input type="text" class="form-control"
                                .value="${this._userForm.last_name}"
                                @input="${(e) => (this._userForm = { ...this._userForm, last_name: e.target.value })}">
                        </div>
                    </div>
                    <button type="submit" class="btn btn-primary">Create</button>
                </form>
            </div>
        `;
    }

    _renderUsers() {
        return html`
            <div class="page-header">
                <h4>Users</h4>
                <button class="btn btn-primary btn-sm" @click="${this._showNewUserForm}">+ Create User</button>
            </div>
            <p class="page-subtitle">Manage user accounts, roles, and permissions.</p>

            <table class="table table-hover">
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Roles</th>
                        <th>Latest Login</th>
                    </tr>
                </thead>
                <tbody>
                    ${this.users.map(
                        (u) => html`
                        <tr style="cursor: pointer;" @click="${() => this._openUserDetail(u)}">
                            <td>
                                <span style="color: #0d6efd; font-weight: 500;">${u.first_name && u.last_name ? `${u.first_name} ${u.last_name}` : u.username || '-'}</span>
                            </td>
                            <td>${u.email}</td>
                            <td>
                                <div class="badge-list">
                                    ${(u.roles || []).map((r) => html`<span class="badge bg-primary">${r}</span>`)}
                                </div>
                            </td>
                            <td>${u.last_login_at ? this._timeAgo(u.last_login_at) : 'Never'}</td>
                        </tr>
                    `,
                    )}
                    ${!this.users.length ? html`<tr><td colspan="4" class="text-muted">No users</td></tr>` : ''}
                </tbody>
            </table>
        `;
    }

    _timeAgo(dateStr) {
        const d = new Date(dateStr);
        const now = new Date();
        const diff = Math.floor((now.getTime() - d.getTime()) / 1000);
        if (diff < 60) return 'just now';
        if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
        if (diff < 2592000) return `${Math.floor(diff / 86400)} days ago`;
        return d.toLocaleDateString();
    }

    _renderUserDetail() {
        const u = this._userDetail;
        const displayName =
            u.first_name && u.last_name ? `${u.first_name} ${u.last_name}` : u.email;

        return html`
            <a class="back-link" href="/admin/users" @click="${(e) => {
                e.preventDefault();
                this._backToList();
            }}">
                <i class="bi bi-arrow-left"></i> Back to Users
            </a>

            <div class="detail-header">
                <div class="detail-header-info">
                    <h4>${displayName}</h4>
                    <div class="detail-header-meta">
                        <span>user_id: <code>${u.id}</code></span>
                    </div>
                </div>
            </div>

            <ul class="nav nav-tabs mb-4">
                <li class="nav-item">
                    <a class="nav-link ${this._userDetailTab === 'details' ? 'active' : ''}" href="#"
                       @click="${(e) => {
                           e.preventDefault();
                           this._userDetailTab = 'details';
                       }}">Details</a>
                </li>
                <li class="nav-item">
                    <a class="nav-link ${this._userDetailTab === 'roles' ? 'active' : ''}" href="#"
                       @click="${(e) => {
                           e.preventDefault();
                           this._userDetailTab = 'roles';
                           if (!this.roles.length) {
                               api.get('/api/admin/roles').then(
                                   (d) => (this.roles = d.roles || []),
                               );
                           }
                       }}">Roles</a>
                </li>
                <li class="nav-item">
                    <a class="nav-link ${this._userDetailTab === 'permissions' ? 'active' : ''}" href="#"
                       @click="${(e) => {
                           e.preventDefault();
                           this._userDetailTab = 'permissions';
                       }}">Permissions</a>
                </li>
                <li class="nav-item">
                    <a class="nav-link ${this._userDetailTab === 'consents' ? 'active' : ''}" href="#"
                       @click="${(e) => {
                           e.preventDefault();
                           this._userDetailTab = 'consents';
                       }}">Consents</a>
                </li>
                <li class="nav-item">
                    <a class="nav-link ${this._userDetailTab === 'identities' ? 'active' : ''}" href="#"
                       @click="${(e) => {
                           e.preventDefault();
                           this._userDetailTab = 'identities';
                       }}">Connected accounts</a>
                </li>
            </ul>

            ${this._userDetailTab === 'details' ? this._renderUserDetailDetails(u) : ''}
            ${this._userDetailTab === 'roles' ? this._renderUserDetailRoles() : ''}
            ${this._userDetailTab === 'permissions' ? this._renderUserDetailPermissions() : ''}
            ${this._userDetailTab === 'consents' ? this._renderUserDetailConsents() : ''}
            ${this._userDetailTab === 'identities' ? this._renderUserDetailIdentities() : ''}
        `;
    }

    _renderUserDetailDetails(u) {
        return html`
            <div class="settings-section">
                <h5>User Information</h5>

                <div class="row">
                    <div class="col-md-6 mb-3">
                        <label class="form-label">First Name</label>
                        <input type="text" class="form-control"
                            .value="${this._userDetailForm.first_name}"
                            @input="${(e) => this._updateUserDetailForm('first_name', e.target.value)}">
                    </div>
                    <div class="col-md-6 mb-3">
                        <label class="form-label">Last Name</label>
                        <input type="text" class="form-control"
                            .value="${this._userDetailForm.last_name}"
                            @input="${(e) => this._updateUserDetailForm('last_name', e.target.value)}">
                    </div>
                </div>

                <div class="mb-3">
                    <label class="form-label">Email</label>
                    <input type="email" class="form-control"
                        .value="${this._userDetailForm.email}"
                        @input="${(e) => this._updateUserDetailForm('email', e.target.value)}">
                </div>

                <div class="mb-3">
                    <label class="form-label">Username</label>
                    <input type="text" class="form-control"
                        .value="${this._userDetailForm.username}"
                        @input="${(e) => this._updateUserDetailForm('username', e.target.value)}">
                </div>

                <div class="mb-3 form-check">
                    <input type="checkbox" class="form-check-input" id="userEmailVerified"
                        .checked="${this._userDetailForm.email_verified}"
                        @change="${(e) => this._updateUserDetailForm('email_verified', e.target.checked)}">
                    <label class="form-check-label" for="userEmailVerified">Email Verified</label>
                </div>
            </div>

            <div class="settings-section">
                <h5>Metadata</h5>
                <div class="row">
                    <div class="col-md-4 mb-3">
                        <label class="form-label text-muted">User ID</label>
                        <div><code style="font-size: 0.8rem;">${u.id}</code></div>
                    </div>
                    <div class="col-md-4 mb-3">
                        <label class="form-label text-muted">Signed Up</label>
                        <div>${new Date(u.created_at).toLocaleString()}</div>
                    </div>
                    <div class="col-md-4 mb-3">
                        <label class="form-label text-muted">Latest Login</label>
                        <div>${u.last_login_at ? new Date(u.last_login_at).toLocaleString() : 'Never'}</div>
                    </div>
                </div>
            </div>

            <div class="d-flex justify-content-between align-items-center mt-3">
                <button class="btn btn-outline-danger btn-sm" @click="${() => this._deleteUser(u)}">Delete User</button>
                ${
                    this._userDetailDirty
                        ? html`
                    <div class="d-flex gap-2">
                        <button class="btn btn-secondary" @click="${this._resetUserDetailForm}">Cancel</button>
                        <button class="btn btn-primary" @click="${this._saveUserDetail}">Save Changes</button>
                    </div>
                `
                        : ''
                }
            </div>
        `;
    }

    _renderUserDetailRoles() {
        return html`
            <div class="settings-section">
                <h5>Assigned Roles</h5>

                ${
                    this._userDetailRoles.length
                        ? html`
                    <table class="table table-sm mb-3">
                        <thead><tr><th>Role</th><th>Description</th><th></th></tr></thead>
                        <tbody>
                            ${this._userDetailRoles.map(
                                (r) => html`
                                <tr>
                                    <td>${r.name}</td>
                                    <td>${r.description || ''}</td>
                                    <td class="text-end">
                                        <button class="btn btn-outline-danger btn-sm" @click="${() => this._removeUserRole(r.id)}">
                                            <i class="bi bi-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            `,
                            )}
                        </tbody>
                    </table>
                `
                        : html`<p class="text-muted">No roles assigned.</p>`
                }

                ${
                    this.roles.length
                        ? html`
                    <div class="d-flex gap-2 align-items-center">
                        <select class="form-select form-select-sm" style="max-width: 300px;" id="addUserRoleSelect">
                            <option value="">Assign a role...</option>
                            ${this.roles
                                .filter((r) => !this._userDetailRoles.some((ur) => ur.id === r.id))
                                .map((r) => html`<option value="${r.id}">${r.name}</option>`)}
                        </select>
                        <button class="btn btn-primary btn-sm" @click="${() => {
                            /** @type {HTMLSelectElement | null} */
                            const select = this.renderRoot.querySelector('#addUserRoleSelect');
                            if (select?.value) {
                                this._addUserRole(select.value);
                                select.value = '';
                            }
                        }}">Assign Role</button>
                    </div>
                `
                        : ''
                }
            </div>
        `;
    }

    _renderUserDetailPermissions() {
        return html`
            <div class="settings-section">
                <h5>Effective Permissions</h5>
                <p class="text-muted" style="font-size: 0.9rem;">All permissions this user has, from their assigned roles.</p>

                ${
                    this._userDetailPermissions.length
                        ? html`
                    <table class="table table-sm mb-0">
                        <thead><tr><th>Permission</th><th>API</th><th>Source</th></tr></thead>
                        <tbody>
                            ${this._userDetailPermissions.map(
                                (p) => html`
                                <tr>
                                    <td><code>${p.permission}</code></td>
                                    <td>${p.api_name}</td>
                                    <td>${p.source === 'role' ? html`Role: ${p.role_name}` : 'Direct'}</td>
                                </tr>
                            `,
                            )}
                        </tbody>
                    </table>
                `
                        : html`<p class="text-muted mb-0">No permissions.</p>`
                }
            </div>
        `;
    }

    // What this user has agreed to, and what they still owe (#80).
    //
    // The hash and the evidence columns are the point: a consent record is only
    // worth keeping if it can show WHICH wording someone accepted and from
    // where. Without them the tab would answer "did they consent?" but not the
    // question anyone actually asks when it matters.
    _renderUserDetailConsents() {
        const accepted = this._userDetailConsents.accepted || [];
        const outstanding = this._userDetailConsents.outstanding || [];

        return html`
            <div class="settings-section">
                <h5>Accepted</h5>
                <p class="text-muted" style="font-size: 0.9rem;">
                    Every document version this user has agreed to, newest first.
                </p>

                ${
                    accepted.length
                        ? html`
                    <table class="table table-sm mb-0">
                        <thead>
                            <tr>
                                <th>Document</th><th>Version</th><th>Accepted</th>
                                <th>Content hash</th><th>From</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${accepted.map(
                                (c) => html`
                                <tr>
                                    <td><code>${c.document_key}</code></td>
                                    <td>v${c.document_version}</td>
                                    <td>${new Date(c.accepted_at).toLocaleString()}</td>
                                    <td><code title="${c.content_sha256}">${(c.content_sha256 || '').slice(0, 12)}</code></td>
                                    <td>
                                        ${c.ip || '—'}
                                        ${c.user_agent ? html`<div class="text-muted" style="font-size: 0.8rem;">${c.user_agent}</div>` : ''}
                                    </td>
                                </tr>
                            `,
                            )}
                        </tbody>
                    </table>
                `
                        : html`<p class="text-muted mb-0">Nothing accepted yet.</p>`
                }
            </div>

            <div class="settings-section">
                <h5>Outstanding</h5>
                <p class="text-muted" style="font-size: 0.9rem;">
                    Documents required of this user that they have not accepted at the current version.
                </p>

                ${
                    outstanding.length
                        ? html`
                    <table class="table table-sm mb-0">
                        <thead><tr><th>Document</th><th>Version</th><th>Title</th></tr></thead>
                        <tbody>
                            ${outstanding.map(
                                (d) => html`
                                <tr>
                                    <td><code>${d.key}</code></td>
                                    <td>v${d.version}</td>
                                    <td>${d.title}</td>
                                </tr>
                            `,
                            )}
                        </tbody>
                    </table>
                `
                        : html`<p class="text-muted mb-0">Nothing outstanding — this user is current.</p>`
                }
            </div>
        `;
    }

    // The provider accounts this user has linked (#82).
    //
    // The handle comes out of metadata rather than provider_user_id, because a
    // Discord id is a number nobody recognises and the whole point of the tab is
    // answering "is this the right account?". Metadata is provider-shaped and
    // can be empty on a link made before it was recorded, so the id is the
    // fallback rather than the default.
    //
    // username before global_name deliberately: the username is the @handle
    // someone identifies themselves by ("I'm @halfogre"), while global_name is a
    // display name they can set to anything, including someone else's.
    _identityHandle(identity) {
        const metadata = identity.metadata || {};
        return metadata.username || metadata.global_name || identity.provider_user_id;
    }

    _renderUserDetailIdentities() {
        return html`
            <div class="settings-section">
                <h5>Connected accounts</h5>
                <p class="text-muted" style="font-size: 0.9rem;">
                    Provider accounts linked to this user. Unlinked accounts are not listed.
                </p>

                ${
                    this._userDetailIdentities.length
                        ? html`
                    <table class="table table-sm mb-0">
                        <thead>
                            <tr>
                                <th>Provider</th><th>Account</th>
                                <th>Provider email</th><th>Linked</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${this._userDetailIdentities.map(
                                (i) => html`
                                <tr>
                                    <td>${providerLabel(i.provider)}</td>
                                    <td>
                                        ${this._identityHandle(i)}
                                        ${
                                            this._identityHandle(i) === i.provider_user_id
                                                ? ''
                                                : html`<div class="text-muted" style="font-size: 0.8rem;">
                                            <code>${i.provider_user_id}</code>
                                        </div>`
                                        }
                                    </td>
                                    <td>${i.provider_email || '—'}</td>
                                    <td>${new Date(i.created_at).toLocaleString()}</td>
                                </tr>
                            `,
                            )}
                        </tbody>
                    </table>
                `
                        : html`<p class="text-muted mb-0">No connected accounts.</p>`
                }
            </div>
        `;
    }

    _renderAPIs() {
        return html`
            <div class="page-header">
                <h4>APIs</h4>
                <button class="btn btn-primary btn-sm" @click="${this._showNewAPIForm}">+ Create API</button>
            </div>
            <p class="page-subtitle">Define APIs that you can consume from your authorized applications.</p>

            <div class="list-container">
                ${this.apis.map(
                    (a) => html`
                    <div class="list-item" @click="${() => this._openAPIDetail(a)}">
                        <div class="list-item-info">
                            <div class="list-item-name">${a.name}</div>
                            <div class="list-item-detail">${this._isManagementAPI(a) ? 'System API' : 'Custom API'}</div>
                        </div>
                        <div class="list-item-meta">
                            <span>API Audience:</span>
                            <code>${a.identifier}</code>
                        </div>
                    </div>
                `,
                )}
                ${!this.apis.length ? html`<div class="list-item text-muted">No APIs defined</div>` : ''}
            </div>
        `;
    }

    // ========== API Detail view ==========

    _renderAPICreate() {
        return html`
            <a class="back-link" href="/admin/apis" @click="${(e) => {
                e.preventDefault();
                this._backToList();
            }}">
                <i class="bi bi-arrow-left"></i> Back to APIs
            </a>

            <h4 style="font-weight: 600; margin-bottom: 1.5rem;">Create Custom API</h4>

            <div class="settings-section">
                <h5>General Settings</h5>
                <form @submit="${this._handleAPIFormSubmit}">
                    <div class="mb-3">
                        <label class="form-label">Name *</label>
                        <input type="text" class="form-control" required
                            .value="${this._apiForm.name}"
                            @input="${(e) => (this._apiForm = { ...this._apiForm, name: e.target.value })}">
                        <div class="form-text">A friendly name for the API.</div>
                    </div>
                    <div class="mb-3">
                        <label class="form-label">Identifier *</label>
                        <input type="text" class="form-control" required
                            .value="${this._apiForm.identifier}"
                            @input="${(e) => (this._apiForm = { ...this._apiForm, identifier: e.target.value })}"
                            placeholder="https://your-api-endpoint/">
                        <div class="form-text">Unique identifier for the API. This value will be used as the <code>audience</code> parameter on authorization calls. <strong>Identifier cannot be changed later.</strong></div>
                    </div>
                    <div class="mb-3">
                        <label class="form-label">Token Lifetime (seconds)</label>
                        <input type="number" class="form-control" required min="60"
                            .value="${String(this._apiForm.token_lifetime)}"
                            @input="${(e) => (this._apiForm = { ...this._apiForm, token_lifetime: e.target.value })}">
                    </div>
                    <div class="mb-3 form-check">
                        <input type="checkbox" class="form-check-input" id="createAllowOffline"
                            .checked="${this._apiForm.allow_offline_access}"
                            @change="${(e) => (this._apiForm = { ...this._apiForm, allow_offline_access: e.target.checked })}">
                        <label class="form-check-label" for="createAllowOffline">Allow Offline Access</label>
                        <div class="form-text">When enabled, applications can request refresh tokens for this API.</div>
                    </div>
                    <div class="mb-3 form-check">
                        <input type="checkbox" class="form-check-input" id="createAllowSelfRegistered"
                            .checked="${this._apiForm.allow_self_registered_clients}"
                            @change="${(e) => (this._apiForm = { ...this._apiForm, allow_self_registered_clients: e.target.checked })}">
                        <label class="form-check-label" for="createAllowSelfRegistered">Allow Self-Registered Clients</label>
                        <div class="form-text">When enabled, apps nobody created here -- ones that identify themselves with a client metadata document URL, such as Claude -- can ask a user to approve access to this API. Leave off unless the API is meant for them.</div>
                    </div>
                    <button type="submit" class="btn btn-primary">Create</button>
                </form>
            </div>
        `;
    }

    _renderAPIDetail() {
        const a = this._apiDetail;
        const isSystem = this._isManagementAPI(a);

        return html`
            <a class="back-link" href="/admin/apis" @click="${(e) => {
                e.preventDefault();
                this._backToList();
            }}">
                <i class="bi bi-arrow-left"></i> Back to APIs
            </a>

            <div class="detail-header">
                <div class="detail-header-info">
                    <h4>${a.name}</h4>
                    <div class="detail-header-meta">
                        <span>${isSystem ? 'System API' : 'Custom API'}</span>
                        <span>Identifier <code>${a.identifier}</code></span>
                    </div>
                </div>
            </div>

            ${
                isSystem
                    ? html`
                <div class="alert alert-warning">
                    This API represents a system entity and cannot be modified or deleted. You can still authorize applications to consume this API.
                </div>
            `
                    : ''
            }

            <ul class="nav nav-tabs mb-4">
                <li class="nav-item">
                    <a class="nav-link ${this._apiDetailTab === 'settings' ? 'active' : ''}" href="#"
                       @click="${(e) => {
                           e.preventDefault();
                           this._apiDetailTab = 'settings';
                       }}">Settings</a>
                </li>
                <li class="nav-item">
                    <a class="nav-link ${this._apiDetailTab === 'permissions' ? 'active' : ''}" href="#"
                       @click="${(e) => {
                           e.preventDefault();
                           this._apiDetailTab = 'permissions';
                       }}">Permissions</a>
                </li>
            </ul>

            ${this._apiDetailTab === 'settings' ? this._renderAPIDetailSettings(a, isSystem) : ''}
            ${this._apiDetailTab === 'permissions' ? this._renderAPIDetailPermissions() : ''}
        `;
    }

    _renderAPIDetailSettings(a, isSystem) {
        return html`
            <div class="settings-section">
                <h5>General Settings</h5>

                <div class="mb-3">
                    <label class="form-label text-muted">Id</label>
                    <input type="text" class="form-control" disabled .value="${a.id}">
                    <div class="form-text">The API id on our system.</div>
                </div>

                <div class="mb-3">
                    <label class="form-label">Name</label>
                    <input type="text" class="form-control" ?disabled="${isSystem}"
                        .value="${this._apiDetailForm.name}"
                        @input="${(e) => this._updateAPIDetailForm('name', e.target.value)}">
                    <div class="form-text">A friendly name for the API.</div>
                </div>

                <div class="mb-3">
                    <label class="form-label text-muted">Identifier</label>
                    <input type="text" class="form-control" disabled .value="${a.identifier}">
                    <div class="form-text">A unique identifier for the API. This value is used as the audience parameter in authorization calls. Cannot be changed.</div>
                </div>

                <div class="mb-3">
                    <label class="form-label text-muted">Signing Algorithm</label>
                    <input type="text" class="form-control" disabled .value="${a.signing_alg}">
                </div>
            </div>

            <div class="settings-section">
                <h5>Token Settings</h5>

                <div class="mb-3">
                    <label class="form-label">Token Lifetime (seconds)</label>
                    <input type="number" class="form-control" min="60" ?disabled="${isSystem}"
                        .value="${String(this._apiDetailForm.token_lifetime)}"
                        @input="${(e) => this._updateAPIDetailForm('token_lifetime', e.target.value)}">
                    <div class="form-text">How long an access token issued for this API is valid, in seconds.</div>
                </div>
            </div>

            <div class="settings-section">
                <h5>Access Settings</h5>

                <div class="form-check mb-2">
                    <input type="checkbox" class="form-check-input" id="detailAllowOffline" ?disabled="${isSystem}"
                        .checked="${this._apiDetailForm.allow_offline_access}"
                        @change="${(e) => this._updateAPIDetailForm('allow_offline_access', e.target.checked)}">
                    <label class="form-check-label" for="detailAllowOffline">Allow Offline Access</label>
                    <div class="form-text">When enabled, applications can request refresh tokens for this API.</div>
                </div>
                <div class="form-check mb-2">
                    <input type="checkbox" class="form-check-input" id="detailAllowSelfRegistered" ?disabled="${isSystem}"
                        .checked="${this._apiDetailForm.allow_self_registered_clients}"
                        @change="${(e) => this._updateAPIDetailForm('allow_self_registered_clients', e.target.checked)}">
                    <label class="form-check-label" for="detailAllowSelfRegistered">Allow Self-Registered Clients</label>
                    <div class="form-text">When enabled, apps nobody created here -- ones that identify themselves with a client metadata document URL, such as Claude -- can ask a user to approve access to this API. Leave off unless the API is meant for them.</div>
                </div>
            </div>

            ${
                !isSystem
                    ? html`
                <div class="d-flex justify-content-between align-items-center mt-3">
                    <button class="btn btn-outline-danger btn-sm" @click="${() => this._deleteAPI(a)}">Delete this API</button>
                    ${
                        this._apiDetailDirty
                            ? html`
                        <div class="d-flex gap-2">
                            <button class="btn btn-secondary" @click="${() => {
                                this._apiDetailForm = {
                                    name: a.name,
                                    token_lifetime: a.token_lifetime,
                                    allow_offline_access: a.allow_offline_access,
                                    allow_self_registered_clients: a.allow_self_registered_clients,
                                };
                                this._apiDetailDirty = false;
                            }}">Cancel</button>
                            <button class="btn btn-primary" @click="${this._saveAPIDetail}">Save Changes</button>
                        </div>
                    `
                            : ''
                    }
                </div>
            `
                    : ''
            }
        `;
    }

    async _addAPIPermission(e) {
        e.preventDefault();
        const form = e.target;
        const permission = form.querySelector('[name="permission"]').value.trim();
        const description = form.querySelector('[name="description"]').value.trim();
        if (!permission) return;

        try {
            await api.post(`/api/admin/apis/${this._apiDetail.id}/permissions`, {
                permission,
                description: description || null,
            });
            form.reset();
            const data = await api.get(`/api/admin/apis/${this._apiDetail.id}/permissions`);
            this._apiDetailPermissions = data.permissions || [];
        } catch (err) {
            this.error = err.message;
        }
    }

    async _removeAPIPermission(permId) {
        try {
            await api.delete(`/api/admin/apis/${this._apiDetail.id}/permissions/${permId}`);
            const data = await api.get(`/api/admin/apis/${this._apiDetail.id}/permissions`);
            this._apiDetailPermissions = data.permissions || [];
        } catch (err) {
            this.error = err.message;
        }
    }

    _renderAPIDetailPermissions() {
        const isSystem = this._isManagementAPI(this._apiDetail);
        return html`
            <div class="settings-section">
                <h5>Permissions</h5>
                <p class="text-muted" style="font-size: 0.9rem;">Define permissions that can be granted to users via roles.</p>

                ${
                    !isSystem
                        ? html`
                    <form class="d-flex gap-2 mb-3 align-items-end" @submit="${this._addAPIPermission}">
                        <div class="flex-grow-1">
                            <label class="form-label" style="font-size: 0.85rem;">Permission</label>
                            <input type="text" class="form-control form-control-sm" name="permission" required
                                placeholder="read:items">
                        </div>
                        <div class="flex-grow-1">
                            <label class="form-label" style="font-size: 0.85rem;">Description</label>
                            <input type="text" class="form-control form-control-sm" name="description"
                                placeholder="Read all items">
                        </div>
                        <button type="submit" class="btn btn-primary btn-sm">Add</button>
                    </form>
                `
                        : ''
                }

                ${
                    this._apiDetailPermissions.length
                        ? html`
                    <table class="table table-sm mb-0">
                        <thead><tr><th>Permission</th><th>Description</th>${!isSystem ? html`<th></th>` : ''}</tr></thead>
                        <tbody>
                            ${this._apiDetailPermissions.map(
                                (p) => html`
                                <tr>
                                    <td><code>${p.permission}</code></td>
                                    <td>${p.description || ''}</td>
                                    ${
                                        !isSystem
                                            ? html`
                                        <td class="text-end">
                                            <button class="btn btn-outline-danger btn-sm" @click="${() => this._removeAPIPermission(p.id)}">
                                                <i class="bi bi-trash"></i>
                                            </button>
                                        </td>
                                    `
                                            : ''
                                    }
                                </tr>
                            `,
                            )}
                        </tbody>
                    </table>
                `
                        : html`<p class="text-muted mb-0">No permissions defined for this API.</p>`
                }
            </div>
        `;
    }

    // ========== Other list views ==========

    _appTypeLabel(type) {
        return (
            {
                regular_web: 'Regular Web Application',
                spa: 'Single Page Application',
                native: 'Native',
                machine_to_machine: 'Machine to Machine',
            }[type] || type
        );
    }

    _appGrantTypes(type) {
        return (
            {
                regular_web: ['Authorization Code'],
                spa: ['Authorization Code (PKCE)'],
                native: ['Authorization Code (PKCE)'],
                machine_to_machine: ['Client Credentials'],
            }[type] || []
        );
    }

    _renderApplications() {
        return html`
            <div class="page-header">
                <h4>Applications</h4>
                <button class="btn btn-primary btn-sm" @click="${this._showNewAppForm}">+ Create Application</button>
            </div>
            <p class="page-subtitle">Applications that can request access tokens on behalf of users.</p>

            <div class="list-container">
                ${this.applications.map(
                    (a) => html`
                    <div class="list-item" @click="${() => this._openAppDetail(a)}">
                        <div class="list-item-info">
                            <div class="list-item-name">
                                ${a.name}
                                ${a.registration === 'metadata_document' ? html`<span class="badge text-bg-secondary ms-1">Self-registered</span>` : ''}
                            </div>
                            <div class="list-item-detail">${this._appTypeLabel(a.app_type)}</div>
                        </div>
                        <div class="list-item-meta">
                            <span>Client ID:</span>
                            <code>${a.client_id}</code>
                        </div>
                    </div>
                `,
                )}
                ${!this.applications.length ? html`<div class="list-item text-muted">No applications</div>` : ''}
            </div>
        `;
    }

    _renderAppCreate() {
        return html`
            <a class="back-link" href="/admin/applications" @click="${(e) => {
                e.preventDefault();
                this._backToList();
            }}">
                <i class="bi bi-arrow-left"></i> Back to Applications
            </a>

            <h4 style="font-weight: 600; margin-bottom: 1.5rem;">Create Application</h4>

            <div class="settings-section">
                <h5>Basic Information</h5>
                <form @submit="${this._handleAppFormSubmit}">
                    <div class="mb-3">
                        <label class="form-label">Name *</label>
                        <input type="text" class="form-control" required
                            .value="${this._appForm.name}"
                            @input="${(e) => (this._appForm = { ...this._appForm, name: e.target.value })}">
                        <div class="form-text">A friendly name for this application.</div>
                    </div>
                    <div class="mb-3">
                        <label class="form-label">Client ID *</label>
                        <input type="text" class="form-control" required
                            .value="${this._appForm.client_id}"
                            @input="${(e) => (this._appForm = { ...this._appForm, client_id: e.target.value })}"
                            placeholder="my-app">
                        <div class="form-text">A unique identifier for this application. <strong>Cannot be changed later.</strong></div>
                    </div>
                    <div class="mb-3">
                        <label class="form-label">Application Type *</label>
                        <select class="form-select"
                            .value="${this._appForm.app_type}"
                            @change="${(e) => (this._appForm = { ...this._appForm, app_type: e.target.value })}">
                            <option value="regular_web">Regular Web Application</option>
                            <option value="spa">Single Page Application</option>
                            <option value="native">Native</option>
                            <option value="machine_to_machine">Machine to Machine</option>
                        </select>
                    </div>
                    <div class="mb-3">
                        <label class="form-label">Allowed Callback URLs</label>
                        <textarea class="form-control" rows="2"
                            .value="${this._appForm.redirect_uris}"
                            @input="${(e) => (this._appForm = { ...this._appForm, redirect_uris: e.target.value })}"
                            placeholder="https://example.com/callback"></textarea>
                        <div class="form-text">One URL per line.</div>
                    </div>
                    <button type="submit" class="btn btn-primary">Create</button>
                </form>
            </div>
        `;
    }

    _renderAppDetail() {
        const a = this._appDetail;

        return html`
            <a class="back-link" href="/admin/applications" @click="${(e) => {
                e.preventDefault();
                this._backToList();
            }}">
                <i class="bi bi-arrow-left"></i> Back to Applications
            </a>

            <div class="detail-header">
                <div class="detail-header-info">
                    <h4>${a.name}</h4>
                    <div class="detail-header-meta">
                        <span>${this._appTypeLabel(a.app_type)}</span>
                        <span>Client ID <code>${a.client_id}</code></span>
                    </div>
                </div>
            </div>

            ${this._renderRevealedSecret(a)}

            <ul class="nav nav-tabs mb-4">
                <li class="nav-item">
                    <a class="nav-link ${this._appDetailTab === 'settings' ? 'active' : ''}" href="#"
                       @click="${(e) => {
                           e.preventDefault();
                           this._appDetailTab = 'settings';
                       }}">Settings</a>
                </li>
                <li class="nav-item">
                    <a class="nav-link ${this._appDetailTab === 'apis' ? 'active' : ''}" href="#"
                       @click="${async (e) => {
                           e.preventDefault();
                           this._appDetailTab = 'apis';
                           if (!this.apis.length) {
                               const d = await api.get('/api/admin/apis');
                               this.apis = d.apis || [];
                           }
                       }}">APIs</a>
                </li>
            </ul>

            ${this._appDetailTab === 'settings' ? this._renderAppDetailSettings(a) : ''}
            ${this._appDetailTab === 'apis' ? this._renderAppDetailAPIs() : ''}
        `;
    }

    _renderRevealedSecret(a) {
        const revealed = revealedSecretFor(this._revealedSecret, a.id);
        if (!revealed) return '';

        return html`
            <div class="alert alert-warning" role="alert">
                <div class="d-flex justify-content-between align-items-start mb-2">
                    <strong><i class="bi bi-key"></i> Client secret for ${revealed.name}</strong>
                    <button type="button" class="btn-close" aria-label="Dismiss"
                        @click="${this._dismissRevealedSecret}"></button>
                </div>
                <p class="mb-2">
                    Copy this secret now. It won't be shown again. If you lose it, rotate the secret to get a new one.
                </p>
                <div class="input-group">
                    <input type="text" class="form-control font-monospace" readonly
                        .value="${revealed.secret}" @focus="${(e) => e.target.select()}">
                    <button class="btn btn-outline-secondary" type="button" @click="${this._copyRevealedSecret}">
                        <i class="bi ${this._secretCopied ? 'bi-check2' : 'bi-clipboard'}"></i>
                        ${this._secretCopied ? 'Copied' : 'Copy'}
                    </button>
                </div>
            </div>
        `;
    }

    _renderAppDetailSettings(a) {
        return html`
            <div class="settings-section">
                <h5>Basic Information</h5>

                <div class="mb-3">
                    <label class="form-label text-muted">Client ID</label>
                    <input type="text" class="form-control" disabled .value="${a.client_id}">
                </div>

                ${
                    isConfidentialAppType(a.app_type)
                        ? html`
                    <div class="mb-3">
                        <label class="form-label text-muted">Client Secret</label>
                        <div class="input-group">
                            <input type="text" class="form-control" disabled value="••••••••••••••••">
                            <button class="btn btn-outline-danger" type="button"
                                @click="${() => this._rotateAppSecret(a)}">Rotate secret</button>
                        </div>
                        <div class="form-text">The secret is shown only when it is created or rotated.</div>
                    </div>
                `
                        : ''
                }

                <div class="mb-3">
                    <label class="form-label">Name</label>
                    <input type="text" class="form-control"
                        .value="${this._appDetailForm.name}"
                        @input="${(e) => this._updateAppDetailForm('name', e.target.value)}">
                </div>

                <div class="mb-3">
                    <label class="form-label">Description</label>
                    <input type="text" class="form-control"
                        .value="${this._appDetailForm.description}"
                        @input="${(e) => this._updateAppDetailForm('description', e.target.value)}">
                </div>

                <div class="mb-3">
                    <label class="form-label">Application Type</label>
                    <select class="form-select"
                        .value="${this._appDetailForm.app_type}"
                        @change="${(e) => this._updateAppDetailForm('app_type', e.target.value)}">
                        <option value="regular_web">Regular Web Application</option>
                        <option value="spa">Single Page Application</option>
                        <option value="native">Native</option>
                        <option value="machine_to_machine">Machine to Machine</option>
                    </select>
                </div>

                <div class="mb-3">
                    <label class="form-label text-muted">Grant Types</label>
                    <div>
                        ${this._appGrantTypes(this._appDetailForm.app_type).map(
                            (g) => html`<span class="badge bg-secondary me-1">${g}</span>`,
                        )}
                    </div>
                    <div class="form-text">Determined by the application type.</div>
                </div>

                <div class="mb-3">
                    <label class="form-label text-muted">Authentication Method</label>
                    <div>
                        <span class="badge bg-secondary">
                            ${
                                this._appDetailForm.app_type === 'regular_web'
                                    ? 'Client Secret'
                                    : this._appDetailForm.app_type === 'machine_to_machine'
                                      ? 'Client Secret'
                                      : 'PKCE'
                            }
                        </span>
                    </div>
                    <div class="form-text">How this application authenticates when exchanging tokens.</div>
                </div>
            </div>

            <div class="settings-section">
                <h5>Application URIs</h5>

                <div class="mb-3">
                    <label class="form-label">Allowed Callback URLs</label>
                    <textarea class="form-control" rows="3"
                        .value="${this._appDetailForm.redirect_uris}"
                        @input="${(e) => this._updateAppDetailForm('redirect_uris', e.target.value)}"></textarea>
                    <div class="form-text">One URL per line. After authentication, only these URLs will be valid for redirects.</div>
                </div>

                <div class="mb-3">
                    <label class="form-label">Allowed Logout URLs</label>
                    <textarea class="form-control" rows="3"
                        .value="${this._appDetailForm.allowed_logout_urls}"
                        @input="${(e) => this._updateAppDetailForm('allowed_logout_urls', e.target.value)}"></textarea>
                    <div class="form-text">One URL per line. After logout, only these URLs are valid for the returnTo parameter.</div>
                </div>

                <div class="mb-3">
                    <label class="form-label">Allowed Web Origins</label>
                    <textarea class="form-control" rows="3"
                        .value="${this._appDetailForm.web_origins}"
                        @input="${(e) => this._updateAppDetailForm('web_origins', e.target.value)}"></textarea>
                    <div class="form-text">One origin per line. Used for CORS.</div>
                </div>

            </div>

            ${
                this._appDetailForm.app_type === 'machine_to_machine'
                    ? html`
            <div class="settings-section">
                <h5>User Lookup</h5>
                <p class="form-text mt-0">
                    The user email lookup answers only for people who have signed in to one of the
                    applications checked here. Check the applications this service's users sign in to.
                </p>
                ${
                    this.applications.filter((other) => other.app_type !== 'machine_to_machine')
                        .length
                        ? this.applications
                              .filter((other) => other.app_type !== 'machine_to_machine')
                              .map(
                                  (other) => html`
                        <div class="form-check">
                            <input type="checkbox" class="form-check-input" id="linked-${other.id}"
                                .checked="${(this._appDetailForm.linked_application_ids || []).includes(other.id)}"
                                @change="${(e) => this._toggleLinkedApplication(other.id, e.target.checked)}">
                            <label class="form-check-label" for="linked-${other.id}">
                                ${other.name} <small class="text-muted">(${other.client_id})</small>
                            </label>
                        </div>
                    `,
                              )
                        : html`<p class="text-muted mb-0">No user-facing applications in this tenant.</p>`
                }
            </div>
            `
                    : ''
            }

            <div class="settings-section">
                <h5>Refresh Token Expiration</h5>

                <div class="mb-3">
                    <label class="form-label">Absolute Lifetime (seconds)</label>
                    <input type="number" class="form-control" min="0"
                        .value="${String(this._appDetailForm.refresh_token_lifetime)}"
                        @input="${(e) => this._updateAppDetailForm('refresh_token_lifetime', e.target.value)}">
                    <div class="form-text">Maximum lifetime of a refresh token, in seconds. After this period, the user must re-authenticate. Default: 2592000 (30 days).</div>
                </div>

                <div class="mb-3">
                    <label class="form-label">Inactivity Lifetime (seconds)</label>
                    <input type="number" class="form-control" min="0"
                        .value="${String(this._appDetailForm.refresh_token_idle_lifetime)}"
                        @input="${(e) => this._updateAppDetailForm('refresh_token_idle_lifetime', e.target.value)}">
                    <div class="form-text">How long a refresh token can remain unused before it expires. Default: 86400 (24 hours).</div>
                </div>
            </div>

            <div class="d-flex justify-content-between align-items-center mt-3">
                <button class="btn btn-outline-danger btn-sm" @click="${() => this._deleteApp(a)}">Delete this Application</button>
                ${
                    this._appDetailDirty
                        ? html`
                    <div class="d-flex gap-2">
                        <button class="btn btn-secondary" @click="${() => {
                            this._appDetailForm = {
                                name: a.name,
                                app_type: a.app_type,
                                redirect_uris: (a.redirect_uris || []).join('\\n'),
                                allowed_logout_urls: (a.allowed_logout_urls || []).join('\\n'),
                                web_origins: (a.web_origins || []).join('\\n'),
                                description: a.description || '',
                                refresh_token_lifetime: a.refresh_token_lifetime,
                                refresh_token_idle_lifetime: a.refresh_token_idle_lifetime,
                                linked_application_ids: a.linked_application_ids || [],
                            };
                            this._appDetailDirty = false;
                        }}">Cancel</button>
                        <button class="btn btn-primary" @click="${this._saveAppDetail}">Save Changes</button>
                    </div>
                `
                        : ''
                }
            </div>
        `;
    }

    _renderAppDetailAPIs() {
        return html`
            <div class="settings-section">
                <h5>Authorized APIs</h5>
                <p class="text-muted" style="font-size: 0.9rem;">APIs this application is allowed to request access tokens for.</p>

                ${
                    this._appDetailAPIs.length
                        ? html`
                    <table class="table table-sm mb-3">
                        <thead><tr><th>Name</th><th>Identifier</th><th></th></tr></thead>
                        <tbody>
                            ${this._appDetailAPIs.map(
                                (a) => html`
                                <tr>
                                    <td>${a.name}</td>
                                    <td><code>${a.identifier}</code></td>
                                    <td class="text-end">
                                        <button class="btn btn-outline-danger btn-sm" @click="${() => this._removeAppAPIGrant(a.id)}">
                                            <i class="bi bi-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            `,
                            )}
                        </tbody>
                    </table>
                `
                        : html`<p class="text-muted">No APIs authorized.</p>`
                }

                ${
                    this.apis.length
                        ? html`
                    <div class="d-flex gap-2 align-items-center">
                        <select class="form-select form-select-sm" style="max-width: 300px;" id="addApiGrantSelect">
                            <option value="">Select an API to authorize...</option>
                            ${this.apis
                                .filter((a) => !this._appDetailAPIs.some((g) => g.id === a.id))
                                .map((a) => html`<option value="${a.id}">${a.name}</option>`)}
                        </select>
                        <button class="btn btn-primary btn-sm" @click="${() => {
                            /** @type {HTMLSelectElement | null} */
                            const select = this.renderRoot.querySelector('#addApiGrantSelect');
                            if (select?.value) {
                                this._addAppAPIGrant(select.value);
                                select.value = '';
                            }
                        }}">Authorize API</button>
                    </div>
                `
                        : ''
                }
            </div>
        `;
    }

    _renderRoles() {
        return html`
            <div class="page-header">
                <h4>Roles</h4>
                <button class="btn btn-primary btn-sm" @click="${this._showNewRoleForm}">+ Create Role</button>
            </div>
            <p class="page-subtitle">Roles group permissions and can be assigned to users.</p>

            <div class="list-container">
                ${this.roles.map(
                    (r) => html`
                    <div class="list-item" @click="${() => this._openRoleDetail(r)}">
                        <div class="list-item-info">
                            <div class="list-item-name">${r.name}</div>
                            <div class="list-item-detail">${r.description || ''}</div>
                        </div>
                    </div>
                `,
                )}
                ${!this.roles.length ? html`<div class="list-item text-muted">No roles defined</div>` : ''}
            </div>
        `;
    }

    _renderRoleCreate() {
        return html`
            <a class="back-link" href="/admin/roles" @click="${(e) => {
                e.preventDefault();
                this._backToList();
            }}">
                <i class="bi bi-arrow-left"></i> Back to Roles
            </a>

            <h4 style="font-weight: 600; margin-bottom: 1.5rem;">Create Role</h4>

            <div class="settings-section">
                <form @submit="${this._handleRoleFormSubmit}">
                    <div class="mb-3">
                        <label class="form-label">Name *</label>
                        <input type="text" class="form-control" required
                            .value="${this._roleForm.name}"
                            @input="${(e) => (this._roleForm = { ...this._roleForm, name: e.target.value })}">
                    </div>
                    <div class="mb-3">
                        <label class="form-label">Description</label>
                        <input type="text" class="form-control"
                            .value="${this._roleForm.description}"
                            @input="${(e) => (this._roleForm = { ...this._roleForm, description: e.target.value })}">
                    </div>
                    <button type="submit" class="btn btn-primary">Create</button>
                </form>
            </div>
        `;
    }

    _renderRoleDetail() {
        const r = this._roleDetail;
        return html`
            <a class="back-link" href="/admin/roles" @click="${(e) => {
                e.preventDefault();
                this._backToList();
            }}">
                <i class="bi bi-arrow-left"></i> Back to Roles
            </a>

            <div class="detail-header">
                <div class="detail-header-info">
                    <h4>${r.name}</h4>
                    <div class="detail-header-meta">
                        <span>${r.description || ''}</span>
                    </div>
                </div>
            </div>

            <ul class="nav nav-tabs mb-4">
                <li class="nav-item">
                    <a class="nav-link ${this._roleDetailTab === 'settings' ? 'active' : ''}" href="#"
                       @click="${(e) => {
                           e.preventDefault();
                           this._roleDetailTab = 'settings';
                       }}">Settings</a>
                </li>
                <li class="nav-item">
                    <a class="nav-link ${this._roleDetailTab === 'permissions' ? 'active' : ''}" href="#"
                       @click="${async (e) => {
                           e.preventDefault();
                           this._roleDetailTab = 'permissions';
                           if (!this.apis.length) {
                               const d = await api.get('/api/admin/apis');
                               this.apis = d.apis || [];
                           }
                       }}">Permissions</a>
                </li>
            </ul>

            ${this._roleDetailTab === 'settings' ? this._renderRoleDetailSettings(r) : ''}
            ${this._roleDetailTab === 'permissions' ? this._renderRoleDetailPermissions() : ''}
        `;
    }

    _renderRoleDetailSettings(r) {
        return html`
            <div class="settings-section">
                <h5>Role Settings</h5>

                <div class="mb-3">
                    <label class="form-label text-muted">Id</label>
                    <input type="text" class="form-control" disabled .value="${r.id}">
                </div>

                <div class="mb-3">
                    <label class="form-label">Name</label>
                    <input type="text" class="form-control"
                        .value="${this._roleDetailForm.name}"
                        @input="${(e) => this._updateRoleDetailForm('name', e.target.value)}">
                </div>

                <div class="mb-3">
                    <label class="form-label">Description</label>
                    <input type="text" class="form-control"
                        .value="${this._roleDetailForm.description}"
                        @input="${(e) => this._updateRoleDetailForm('description', e.target.value)}">
                </div>
            </div>

            <div class="d-flex justify-content-between align-items-center mt-3">
                <button class="btn btn-outline-danger btn-sm" @click="${() => this._deleteRole(r)}">Delete this Role</button>
                ${
                    this._roleDetailDirty
                        ? html`
                    <div class="d-flex gap-2">
                        <button class="btn btn-secondary" @click="${() => {
                            this._roleDetailForm = {
                                name: r.name,
                                description: r.description || '',
                            };
                            this._roleDetailDirty = false;
                        }}">Cancel</button>
                        <button class="btn btn-primary" @click="${this._saveRoleDetail}">Save Changes</button>
                    </div>
                `
                        : ''
                }
            </div>
        `;
    }

    _renderRoleDetailPermissions() {
        return html`
            <div class="settings-section">
                <h5>Assigned Permissions</h5>
                <p class="text-muted" style="font-size: 0.9rem;">Permissions granted to users with this role.</p>

                ${
                    this._roleDetailPermissions.length
                        ? html`
                    <table class="table table-sm mb-3">
                        <thead><tr><th>Permission</th><th>API</th><th></th></tr></thead>
                        <tbody>
                            ${this._roleDetailPermissions.map(
                                (p) => html`
                                <tr>
                                    <td><code>${p.permission}</code></td>
                                    <td>${p.api_name}</td>
                                    <td class="text-end">
                                        <button class="btn btn-outline-danger btn-sm" @click="${() => this._removeRolePermission(p.id)}">
                                            <i class="bi bi-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            `,
                            )}
                        </tbody>
                    </table>
                `
                        : html`<p class="text-muted">No permissions assigned.</p>`
                }

                ${
                    this.apis.length
                        ? html`
                    <h6 class="mt-4">Add Permission</h6>
                    ${this.apis.map((a) => {
                        const availablePerms = (a._permissions || []).filter(
                            (p) => !this._roleDetailPermissions.some((rp) => rp.id === p.id),
                        );
                        return html`
                            <div class="mb-2">
                                <button class="btn btn-sm btn-outline-secondary" @click="${async () => {
                                    if (!a._permissions) {
                                        const data = await api.get(
                                            `/api/admin/apis/${a.id}/permissions`,
                                        );
                                        a._permissions = data.permissions || [];
                                        this.requestUpdate();
                                    }
                                }}">${a.name} ${a._permissions ? html`<i class="bi bi-chevron-down"></i>` : html`<i class="bi bi-chevron-right"></i>`}</button>
                                ${
                                    a._permissions
                                        ? html`
                                    <div class="ms-3 mt-1">
                                        ${
                                            availablePerms.length
                                                ? availablePerms.map(
                                                      (p) => html`
                                            <button class="btn btn-sm btn-outline-primary me-1 mb-1" @click="${() => this._addRolePermission(p.id)}">
                                                <code>${p.permission}</code>
                                            </button>
                                        `,
                                                  )
                                                : html`<span class="text-muted" style="font-size: 0.85rem;">All permissions assigned</span>`
                                        }
                                    </div>
                                `
                                        : ''
                                }
                            </div>
                        `;
                    })}
                `
                        : ''
                }
            </div>
        `;
    }

    async _handleCreateInvitation(e) {
        e.preventDefault();
        try {
            // app_name still rides along: it is what the invitation email says,
            // and it is half of the pending-invitation uniqueness key. It comes
            // from the chosen application rather than a second input, so the
            // email and the recorded client can never disagree.
            const app = this.applications.find((a) => a.client_id === this._inviteForm.client_id);
            await api.post('/api/admin/invitations', {
                email: this._inviteForm.email,
                client_id: this._inviteForm.client_id,
                app_name: app ? app.name : '',
                // Omitted rather than sent empty when the admin picked nothing:
                // the API rejects any redirect_uri that is not one of the
                // client's registered callbacks, and "" is not one of them.
                redirect_uri: this._inviteForm.redirect_uri || undefined,
                role_ids: this._inviteForm.role_ids,
                message: this._inviteForm.message || null,
            });
            this._showInviteForm = false;
            this._inviteForm = {
                email: '',
                client_id: '',
                redirect_uri: '',
                role_ids: [],
                message: '',
            };
            this._loadTab();
        } catch (err) {
            this.error = err.message;
        }
    }

    async _resendInvitation(id) {
        try {
            await api.post(`/api/admin/invitations/${id}/resend`);
            this.error = '';
        } catch (err) {
            this.error = err.message;
        }
    }

    async _revokeInvitation(id) {
        try {
            await api.delete(`/api/admin/invitations/${id}`);
            this._loadTab();
        } catch (err) {
            this.error = err.message;
        }
    }

    _toggleInviteRole(roleId) {
        const ids = [...this._inviteForm.role_ids];
        const idx = ids.indexOf(roleId);
        if (idx >= 0) {
            ids.splice(idx, 1);
        } else {
            ids.push(roleId);
        }
        this._inviteForm = { ...this._inviteForm, role_ids: ids };
    }

    // ========== Documents (consent capability, issue #43) ==========

    // Documents are versioned data, not editable records: publishing writes a
    // new immutable version, and bumping a version is what re-gates everyone
    // who accepted the previous one. So there is no edit form here — only
    // publish, and the grants that require each document.
    _renderDocuments() {
        return html`
            <div class="page-header">
                <h4>Documents</h4>
                <button class="btn btn-primary btn-sm" @click="${() => {
                    this._showDocumentForm = !this._showDocumentForm;
                    this._documentForm = { key: '', title: '', body: '', requires_reconsent: true };
                }}">
                    ${this._showDocumentForm ? 'Cancel' : '+ Publish Document'}
                </button>
            </div>
            <p class="page-subtitle">
                Review-and-acknowledge documents. Publishing a new version of an existing
                key re-gates everyone who accepted the previous one.
            </p>

            ${this._showDocumentForm ? this._renderDocumentForm() : ''}

            <div class="list-container mb-4">
                ${this._documents.map(
                    (d) => html`
                    <div class="list-item d-flex align-items-center justify-content-between">
                        <span>
                            <strong>${d.title}</strong>
                            <code class="ms-2">${d.key}</code>
                        </span>
                        <span>
                            ${d.requires_reconsent ? '' : html`<span class="badge bg-info me-1">Notice-only</span>`}
                            <span class="badge bg-secondary">v${d.version}</span>
                        </span>
                    </div>
                `,
                )}
                ${!this._documents.length ? html`<div class="list-item text-muted">No documents published</div>` : ''}
            </div>

            ${this._renderDocumentRequirements()}
        `;
    }

    _renderDocumentForm() {
        return html`
            <div class="form-panel mb-4">
                <form @submit="${this._handlePublishDocument}">
                    <div class="mb-2">
                        <label class="form-label" for="doc-key">Key</label>
                        <input class="form-control" id="doc-key" .value="${this._documentForm.key}"
                            placeholder="privacy-notice"
                            @input="${(e) => {
                                this._documentForm = { ...this._documentForm, key: e.target.value };
                            }}">
                        <small class="text-muted">
                            Reusing an existing key publishes the next version of that document.
                        </small>
                    </div>
                    <div class="mb-2">
                        <label class="form-label" for="doc-title">Title</label>
                        <input class="form-control" id="doc-title" .value="${this._documentForm.title}"
                            @input="${(e) => {
                                this._documentForm = {
                                    ...this._documentForm,
                                    title: e.target.value,
                                };
                            }}">
                    </div>
                    <div class="mb-3">
                        <label class="form-label" for="doc-body">Text</label>
                        <textarea class="form-control" id="doc-body" rows="10" .value="${this._documentForm.body}"
                            @input="${(e) => {
                                this._documentForm = {
                                    ...this._documentForm,
                                    body: e.target.value,
                                };
                            }}"></textarea>
                    </div>
                    ${this._renderReconsentChoice()}
                    <button class="btn btn-primary btn-sm" type="submit"
                        ?disabled="${!this._documentForm.key || !this._documentForm.title || !this._documentForm.body}">
                        Publish
                    </button>
                </form>
            </div>
        `;
    }

    // Publishing an existing key can be a notice-only change: a new version
    // that does NOT re-gate people who already accepted a close-enough one. A
    // FIRST version never can — the server forces it to require
    // re-acknowledgement, because otherwise the document would have no consent
    // floor and would gate nobody — so the choice is locked on for a new key
    // rather than offered and then overridden.
    _renderReconsentChoice() {
        const firstVersion = isFirstVersionOfKey(this._documentForm.key, this._documents);

        return html`
            <div class="form-check mb-3">
                <input class="form-check-input" type="checkbox" id="doc-reconsent"
                    .checked="${firstVersion || this._documentForm.requires_reconsent}"
                    ?disabled="${firstVersion}"
                    @change="${(e) => {
                        this._documentForm = {
                            ...this._documentForm,
                            requires_reconsent: e.target.checked,
                        };
                    }}">
                <label class="form-check-label" for="doc-reconsent">
                    Requires re-acknowledgement
                </label>
                <div>
                    <small class="text-muted">
                        ${
                            firstVersion
                                ? html`A document's first version always requires re-acknowledgement.`
                                : html`Leave unchecked for a notice-only change (a typo or clarification):
                                       everyone who accepted a previous version stays current.`
                        }
                    </small>
                </div>
            </div>
        `;
    }

    async _handlePublishDocument(e) {
        e.preventDefault();
        this.error = '';
        try {
            await api.post('/api/admin/documents', {
                ...this._documentForm,
                requires_reconsent:
                    isFirstVersionOfKey(this._documentForm.key, this._documents) ||
                    this._documentForm.requires_reconsent,
            });
            this._showDocumentForm = false;
            this._documentForm = { key: '', title: '', body: '', requires_reconsent: true };
            await this._loadTab();
        } catch (err) {
            this.error = err.message;
        }
    }

    // Which grant requires which document. A requirement with no permission is
    // required of every user in the tenant.
    _renderDocumentRequirements() {
        return html`
            <h5 class="mt-4">Required for</h5>
            <div class="list-container mb-3">
                ${this._documentRequirements.map(
                    (r) => html`
                    <div class="list-item d-flex align-items-center justify-content-between">
                        <span>
                            <code>${r.document_key}</code>
                            <i class="bi bi-arrow-right mx-2"></i>
                            ${
                                r.permission
                                    ? html`<code>${r.permission}</code>`
                                    : html`<span class="badge bg-info">All users</span>`
                            }
                        </span>
                        <button class="btn btn-sm btn-outline-danger"
                            @click="${() => this._confirmRemoveRequirement(r)}">Remove</button>
                    </div>
                `,
                )}
                ${!this._documentRequirements.length ? html`<div class="list-item text-muted">No documents are required yet</div>` : ''}
            </div>

            ${this._documents.length ? this._renderAddRequirement() : ''}
        `;
    }

    _renderAddRequirement() {
        return html`
            <h6 class="mt-3">Add Requirement</h6>
            <div class="mb-2">
                <label class="form-label" for="req-doc">Document</label>
                <select class="form-select" id="req-doc" .value="${this._requirementFor}"
                    @change="${(e) => {
                        this._requirementFor = e.target.value;
                    }}">
                    <option value="">Select a document…</option>
                    ${this._documents.map((d) => html`<option value="${d.key}">${d.title}</option>`)}
                </select>
            </div>
            ${
                this._requirementFor
                    ? html`
                <button class="btn btn-sm btn-outline-info mb-2"
                    @click="${() => this._addRequirement(null)}">
                    Require of all users in this tenant
                </button>
                <div class="mb-1"><small class="text-muted">…or require it of one permission:</small></div>
                ${this.apis.map(
                    (a) => html`
                    <div class="mb-2">
                        <button class="btn btn-sm btn-outline-secondary" @click="${async () => {
                            if (!a._permissions) {
                                const data = await api.get(`/api/admin/apis/${a.id}/permissions`);
                                a._permissions = data.permissions || [];
                                this.requestUpdate();
                            }
                        }}">
                            ${a.name}
                            ${a._permissions ? html`<i class="bi bi-chevron-down"></i>` : html`<i class="bi bi-chevron-right"></i>`}
                        </button>
                        ${
                            a._permissions
                                ? html`
                            <div class="ms-3 mt-1">
                                ${
                                    a._permissions.length
                                        ? a._permissions.map(
                                              (perm) => html`
                                    <button class="btn btn-sm btn-outline-primary me-1 mb-1"
                                        @click="${() => this._addRequirement(perm.id)}">
                                        <code>${perm.permission}</code>
                                    </button>
                                `,
                                          )
                                        : html`<span class="text-muted" style="font-size: 0.85rem;">No permissions defined</span>`
                                }
                            </div>
                        `
                                : ''
                        }
                    </div>
                `,
                )}
            `
                    : ''
            }
        `;
    }

    async _addRequirement(apiPermissionId) {
        this.error = '';
        try {
            await api.post('/api/admin/document-requirements', {
                document_key: this._requirementFor,
                api_permission_id: apiPermissionId,
            });
            this._requirementFor = '';
            await this._loadTab();
        } catch (err) {
            this.error = err.message;
        }
    }

    _confirmRemoveRequirement(requirement) {
        const who = requirement.permission || 'all users in this tenant';
        this._confirmMessage = `Stop requiring ${requirement.document_key} for ${who}?`;
        this._confirmCallback = async () => {
            try {
                await api.delete(`/api/admin/document-requirements/${requirement.id}`);
                await this._loadTab();
            } catch (err) {
                this.error = err.message;
            }
        };
    }

    _renderSettings() {
        const statusBadge = (status) => {
            const colors = {
                pending: 'bg-warning text-dark',
                accepted: 'bg-success',
                revoked: 'bg-secondary',
            };
            return html`<span class="badge ${colors[status] || 'bg-secondary'}">${status}</span>`;
        };

        // The destinations this invitation may name: the chosen application's
        // registered callbacks, and only those. The API validates by exact
        // match against the same list (#111), so offering free text would be
        // offering a 400.
        const inviteApp = this.applications.find((a) => a.client_id === this._inviteForm.client_id);
        const inviteAppRedirectURIs = inviteApp ? inviteApp.redirect_uris || [] : [];

        return html`
            <div class="page-header">
                <h4>Invitations</h4>
                <button class="btn btn-primary btn-sm" @click="${() => {
                    this._showInviteForm = true;
                }}">+ Create Invitation</button>
            </div>
            <p class="page-subtitle">Invite users to sign up. Invited users are automatically assigned the specified roles on signup. All @dicedriven.com addresses can sign up without an invitation.</p>

            ${
                this._showInviteForm
                    ? html`
                <div class="form-panel mb-3">
                    <h5>New Invitation</h5>
                    <form @submit="${this._handleCreateInvitation}">
                        <div class="mb-3">
                            <label class="form-label">Email *</label>
                            <input type="email" class="form-control" required
                                .value="${this._inviteForm.email}"
                                @input="${(e) => (this._inviteForm = { ...this._inviteForm, email: e.target.value })}">
                        </div>
                        <div class="mb-3">
                            <label class="form-label">Application *</label>
                            <select class="form-select" required
                                .value="${this._inviteForm.client_id}"
                                @change="${(e) => (this._inviteForm = { ...this._inviteForm, client_id: e.target.value, redirect_uri: '' })}">
                                <option value="">Select an application...</option>
                                ${this.applications.map(
                                    (a) => html`
                                    <option value="${a.client_id}"
                                        ?selected="${this._inviteForm.client_id === a.client_id}">${a.name}</option>
                                `,
                                )}
                            </select>
                            <div class="form-text">
                                Names the app in the invitation email, and whose registered
                                callbacks the destination below is chosen from.
                            </div>
                        </div>
                        <div class="mb-3">
                            <label class="form-label">Redirect URI</label>
                            <select class="form-select"
                                .value="${this._inviteForm.redirect_uri}"
                                @change="${(e) => (this._inviteForm = { ...this._inviteForm, redirect_uri: e.target.value })}"
                                ?disabled="${!inviteAppRedirectURIs.length}">
                                <option value="">No destination — the invitee stops at their profile</option>
                                ${inviteAppRedirectURIs.map(
                                    (uri) => html`
                                    <option value="${uri}"
                                        ?selected="${this._inviteForm.redirect_uri === uri}">${uri}</option>
                                `,
                                )}
                            </select>
                            <div class="form-text">
                                Where this invitee lands once they have a session. Only the chosen
                                app's registered callbacks are offered — the API rejects anything
                                else, so a new destination has to be registered on the application
                                first.
                            </div>
                        </div>
                        <div class="mb-3">
                            <label class="form-label">Roles *</label>
                            <div>
                                ${this.roles.map(
                                    (r) => html`
                                    <div class="form-check">
                                        <input type="checkbox" class="form-check-input" id="invite-role-${r.id}"
                                            .checked="${this._inviteForm.role_ids.includes(r.id)}"
                                            @change="${() => this._toggleInviteRole(r.id)}">
                                        <label class="form-check-label" for="invite-role-${r.id}">${r.name}</label>
                                    </div>
                                `,
                                )}
                            </div>
                        </div>
                        <div class="mb-3">
                            <label class="form-label">Message</label>
                            <textarea class="form-control" rows="2"
                                .value="${this._inviteForm.message}"
                                @input="${(e) => (this._inviteForm = { ...this._inviteForm, message: e.target.value })}"
                                placeholder="Optional personal message included in the invitation email"></textarea>
                        </div>
                        <div class="d-flex gap-2">
                            <button type="submit" class="btn btn-primary btn-sm" ?disabled="${!this._inviteForm.role_ids.length || !this._inviteForm.client_id}">Send Invitation</button>
                            <button type="button" class="btn btn-secondary btn-sm" @click="${() => (this._showInviteForm = false)}">Cancel</button>
                        </div>
                    </form>
                </div>
            `
                    : ''
            }

            <table class="table table-hover">
                <thead>
                    <tr><th>Email</th><th>App</th><th>Status</th><th>Created</th><th></th></tr>
                </thead>
                <tbody>
                    ${this._invitations.map(
                        (inv) => html`
                        <tr>
                            <td>${inv.email}</td>
                            <td>${inv.app_name}</td>
                            <td>${statusBadge(inv.status)}</td>
                            <td>${new Date(inv.created_at).toLocaleDateString()}</td>
                            <td class="text-end">
                                ${
                                    inv.status === 'pending'
                                        ? html`
                                    <button class="btn btn-outline-secondary btn-sm me-1" @click="${() => this._resendInvitation(inv.id)}">Resend</button>
                                    <button class="btn btn-outline-danger btn-sm" @click="${() => this._revokeInvitation(inv.id)}">Revoke</button>
                                `
                                        : ''
                                }
                            </td>
                        </tr>
                    `,
                    )}
                    ${!this._invitations.length ? html`<tr><td colspan="5" class="text-muted">No invitations</td></tr>` : ''}
                </tbody>
            </table>
        `;
    }
    // ========== Tenants ==========

    _renderTenants() {
        if (this._tenantDetail) return this._renderTenantDetail();

        return html`
            <div class="page-header">
                <h4>Tenants</h4>
                <button class="btn btn-primary btn-sm" @click="${() => {
                    this._showTenantForm = !this._showTenantForm;
                }}">
                    <i class="bi bi-plus-lg"></i> Create Tenant
                </button>
            </div>
            <p class="page-subtitle">Manage platform tenants. Each tenant has isolated users, apps, APIs, and roles.</p>

            ${
                this._showTenantForm
                    ? html`
                <div class="form-panel">
                    <form @submit="${this._handleTenantCreate}">
                        <div class="row mb-3">
                            <div class="col">
                                <label class="form-label">Name</label>
                                <input type="text" class="form-control" required
                                    .value="${this._tenantForm.name}"
                                    @input="${(e) => (this._tenantForm = { ...this._tenantForm, name: e.target.value })}">
                            </div>
                            <div class="col">
                                <label class="form-label">Slug</label>
                                <input type="text" class="form-control" required pattern="[a-z0-9-]+"
                                    .value="${this._tenantForm.slug}"
                                    @input="${(e) => (this._tenantForm = { ...this._tenantForm, slug: e.target.value.toLowerCase() })}">
                            </div>
                        </div>
                        <button type="submit" class="btn btn-primary btn-sm">Create</button>
                        <button type="button" class="btn btn-link btn-sm" @click="${() => (this._showTenantForm = false)}">Cancel</button>
                    </form>
                </div>
            `
                    : ''
            }

            <div class="list-container">
                ${(this._tenants || []).map(
                    (t) => html`
                    <div class="list-item" @click="${() => this._openTenantDetail(t)}">
                        <div class="list-item-icon"><i class="bi bi-building"></i></div>
                        <div class="list-item-info">
                            <div class="list-item-name">${t.name}</div>
                            <div class="list-item-detail">${t.slug}</div>
                        </div>
                        <div class="list-item-meta">
                            <code>${t.id.substring(0, 8)}...</code>
                        </div>
                    </div>
                `,
                )}
                ${!this._tenants?.length ? html`<div class="p-3 text-muted">No tenants</div>` : ''}
            </div>
        `;
    }

    _renderTenantDetail() {
        const t = this._tenantDetail;
        const isD3 = t.id === 'd3d3d3d3-d3d3-4d3d-bd3d-d3d3d3d3d3d3';
        return html`
            <a class="back-link" href="/admin/tenants" @click="${(e) => {
                e.preventDefault();
                this._tenantDetail = null;
            }}">
                <i class="bi bi-arrow-left"></i> Back to Tenants
            </a>

            <div class="detail-header">
                <div class="detail-header-icon"><i class="bi bi-building"></i></div>
                <div class="detail-header-info">
                    <h4>${t.name}</h4>
                    <div class="detail-header-meta">
                        <span>Slug: <code>${t.slug}</code></span>
                        <span>ID: <code>${t.id}</code></span>
                    </div>
                </div>
            </div>

            <div class="settings-section">
                <h5>Settings</h5>
                <div class="row mb-3">
                    <div class="col">
                        <label class="form-label">Name</label>
                        <input type="text" class="form-control"
                            .value="${this._tenantDetailForm.name}"
                            @input="${(e) => {
                                this._tenantDetailForm = {
                                    ...this._tenantDetailForm,
                                    name: e.target.value,
                                };
                                this._tenantDetailDirty = true;
                            }}">
                    </div>
                    <div class="col">
                        <label class="form-label">Slug</label>
                        <input type="text" class="form-control" pattern="[a-z0-9-]+"
                            .value="${this._tenantDetailForm.slug}"
                            @input="${(e) => {
                                this._tenantDetailForm = {
                                    ...this._tenantDetailForm,
                                    slug: e.target.value.toLowerCase(),
                                };
                                this._tenantDetailDirty = true;
                            }}">
                    </div>
                </div>
                <button class="btn btn-primary btn-sm" ?disabled="${!this._tenantDetailDirty}" @click="${this._handleTenantUpdate}">Save Changes</button>
            </div>

            <div class="settings-section mt-3">
                <h5>Metadata</h5>
                <table class="table table-sm mb-0">
                    <tbody>
                        <tr><td class="text-muted" style="width: 140px;">Tenant ID</td><td><code>${t.id}</code></td></tr>
                        <tr><td class="text-muted">Created</td><td>${new Date(t.created_at).toLocaleString()}</td></tr>
                        <tr><td class="text-muted">Updated</td><td>${new Date(t.updated_at).toLocaleString()}</td></tr>
                    </tbody>
                </table>
            </div>

            ${
                !isD3
                    ? html`
                <div class="settings-section mt-3">
                    <h5>Danger Zone</h5>
                    <p class="text-muted" style="font-size: 0.9rem;">Deleting a tenant is permanent and cannot be undone.</p>
                    <button class="btn btn-outline-danger btn-sm" @click="${() => {
                        this._confirmMessage = `Are you sure you want to delete the tenant "${t.name}"?`;
                        this._confirmCallback = () => this._handleTenantDelete(t.id);
                    }}">
                        Delete Tenant
                    </button>
                </div>
            `
                    : ''
            }
        `;
    }

    _openTenantDetail(tenant) {
        this._tenantDetail = tenant;
        this._tenantDetailForm = { name: tenant.name, slug: tenant.slug };
        this._tenantDetailDirty = false;
    }

    async _handleTenantCreate(e) {
        e.preventDefault();
        try {
            await api.post('/api/staff/tenants', this._tenantForm);
            this._tenantForm = { name: '', slug: '' };
            this._showTenantForm = false;
            this._loadTab();
        } catch (err) {
            this.error = err.message;
        }
    }

    async _handleTenantUpdate() {
        try {
            const updated = await api.put(
                `/api/staff/tenants/${this._tenantDetail.id}`,
                this._tenantDetailForm,
            );
            this._tenantDetail = updated;
            this._tenantDetailDirty = false;
            this._loadTab();
        } catch (err) {
            this.error = err.message;
        }
    }

    async _handleTenantDelete(id) {
        try {
            await api.delete(`/api/staff/tenants/${id}`);
            this._tenantDetail = null;
            this._loadTab();
        } catch (err) {
            this.error = err.message;
        }
    }

    // ========== Event Log ==========

    async _loadEvents() {
        const offset = this._eventsOffset || 0;
        const data = await api.get(`/api/admin/events?limit=50&offset=${offset}`);
        this._events = data.events || [];
        this._eventsTotal = data.total || 0;
    }

    async _loadPlatformEvents() {
        const offset = this._platformEventsOffset || 0;
        const data = await api.get(`/api/staff/events?limit=50&offset=${offset}`);
        this._platformEvents = data.events || [];
        this._platformEventsTotal = data.total || 0;
    }

    _renderPlatformEventLog() {
        const events = this._platformEvents || [];
        const total = this._platformEventsTotal || 0;
        const offset = this._platformEventsOffset || 0;
        return this._renderEventLogTable(
            events,
            total,
            offset,
            'All platform events across all tenants.',
            (newOffset) => {
                this._platformEventsOffset = newOffset;
                this._loadPlatformEvents();
            },
        );
    }

    _renderEventLog() {
        const events = this._events || [];
        const total = this._eventsTotal || 0;
        const offset = this._eventsOffset || 0;
        return this._renderEventLogTable(
            events,
            total,
            offset,
            'Audit trail of all state changes in this tenant.',
            (newOffset) => {
                this._eventsOffset = newOffset;
                this._loadEvents();
            },
        );
    }

    _renderEventLogTable(events, total, offset, subtitle, onPageChange) {
        const hasNext = offset + 50 < total;
        const hasPrev = offset > 0;

        return html`
            <div class="page-header">
                <h4>Event Log</h4>
            </div>
            <p class="page-subtitle">${subtitle} ${total} total events.</p>

            <div class="list-container">
                ${events.map(
                    (e) => html`
                    <div class="list-item" @click="${() => (this._expandedEventId = this._expandedEventId === e.id ? null : e.id)}"
                        style="flex-wrap: wrap;">
                        <div class="list-item-icon">
                            <i class="bi ${this._eventIcon(e.event_type)}"></i>
                        </div>
                        <div class="list-item-info">
                            <div class="list-item-name">
                                <code style="font-size: 0.85rem;">${e.event_type}</code>
                            </div>
                            <div class="list-item-detail">
                                ${e.aggregate_type}/<code>${e.aggregate_id.substring(0, 8)}</code>
                                ${e.actor_display_name ? html` &middot; ${e.actor_display_name}` : ''}
                                ${e.ip ? html` &middot; ${e.ip}` : ''}
                            </div>
                        </div>
                        <div class="list-item-meta" style="text-align: right; min-width: 140px;">
                            <div>${this._formatEventTime(e.created_at)}</div>
                        </div>
                        ${
                            this._expandedEventId === e.id
                                ? html`
                            <div style="width: 100%; border-top: 1px solid #eee; margin-top: 0.75rem; padding-top: 0.75rem; display: flex; gap: 1rem;">
                                <div style="flex: 1;">
                                    <div style="font-size: 0.75rem; font-weight: 600; text-transform: uppercase; color: #999; margin-bottom: 0.5rem;">Metadata</div>
                                    <table style="font-size: 0.8rem; color: #666; border-collapse: collapse; width: 100%;">
                                        <tr>
                                            <td style="padding: 0.15rem 0.75rem 0.15rem 0; font-weight: 600;">Performed by:</td>
                                            <td style="padding: 0.15rem 0;">${e.actor_display_name} <code style="font-size: 0.75rem; color: #999;">${e.actor_id}</code></td>
                                        </tr>
                                        ${
                                            e.ip
                                                ? html`<tr>
                                            <td style="padding: 0.15rem 0.75rem 0.15rem 0; font-weight: 600;">IP:</td>
                                            <td style="padding: 0.15rem 0;">${e.ip}</td>
                                        </tr>`
                                                : ''
                                        }
                                        ${
                                            e.request_id
                                                ? html`<tr>
                                            <td style="padding: 0.15rem 0.75rem 0.15rem 0; font-weight: 600;">Request:</td>
                                            <td style="padding: 0.15rem 0;"><code>${e.request_id}</code></td>
                                        </tr>`
                                                : ''
                                        }
                                        <tr>
                                            <td style="padding: 0.15rem 0.75rem 0.15rem 0; font-weight: 600;">Event ID:</td>
                                            <td style="padding: 0.15rem 0;"><code>${e.id}</code></td>
                                        </tr>
                                        <tr>
                                            <td style="padding: 0.15rem 0.75rem 0.15rem 0; font-weight: 600;">Timestamp:</td>
                                            <td style="padding: 0.15rem 0;">${new Date(e.created_at).toLocaleString()}</td>
                                        </tr>
                                    </table>
                                </div>
                                <div style="flex: 1;">
                                    <div style="font-size: 0.75rem; font-weight: 600; text-transform: uppercase; color: #999; margin-bottom: 0.5rem;">Event Data</div>
                                    <pre @click="${(ev) => ev.stopPropagation()}" style="background: #f8f9fa; border: 1px solid #dee2e6; border-radius: 4px; padding: 0.75rem; margin: 0; font-size: 0.8rem; overflow-x: auto; white-space: pre-wrap; max-height: 200px; overflow-y: auto;">${JSON.stringify(e.data, null, 2)}</pre>
                                </div>
                            </div>
                        `
                                : ''
                        }
                    </div>
                `,
                )}
                ${!events.length ? html`<div class="p-3 text-muted">No events recorded yet.</div>` : ''}
            </div>

            ${
                total > 50
                    ? html`
                <div class="d-flex justify-content-between align-items-center mt-3">
                    <button class="btn btn-outline-secondary btn-sm" ?disabled="${!hasPrev}"
                        @click="${() => onPageChange(Math.max(0, offset - 50))}">
                        <i class="bi bi-chevron-left"></i> Newer
                    </button>
                    <span class="text-muted" style="font-size: 0.85rem;">
                        Showing ${offset + 1}&ndash;${Math.min(offset + 50, total)} of ${total}
                    </span>
                    <button class="btn btn-outline-secondary btn-sm" ?disabled="${!hasNext}"
                        @click="${() => onPageChange(offset + 50)}">
                        Older <i class="bi bi-chevron-right"></i>
                    </button>
                </div>
            `
                    : ''
            }
        `;
    }

    _aggregateLabel(aggregateType) {
        const labels = {
            user: 'User',
            tenant: 'Tenant',
            api: 'API',
            application: 'Application',
            role: 'Role',
            invitation: 'Invitation',
            login_code: 'Login Code',
            refresh_token: 'Refresh Token',
            authorization_code: 'Authorization Code',
            password_reset_token: 'Password Reset Token',
            pending_profile_change: 'Profile Change',
            email_verification: 'Email Verification',
            allowed_email: 'Allowed Email',
        };
        return labels[aggregateType] || aggregateType;
    }

    _eventIcon(eventType) {
        if (eventType.includes('created')) return 'bi-plus-circle text-success';
        if (eventType.includes('updated') || eventType.includes('changed'))
            return 'bi-pencil text-primary';
        if (
            eventType.includes('deleted') ||
            eventType.includes('revoked') ||
            eventType.includes('removed')
        )
            return 'bi-trash text-danger';
        if (
            eventType.includes('assigned') ||
            eventType.includes('added') ||
            eventType.includes('granted')
        )
            return 'bi-link-45deg text-info';
        if (eventType.includes('accepted')) return 'bi-check-circle text-success';
        if (eventType.includes('login') || eventType.includes('verified'))
            return 'bi-shield-check text-warning';
        return 'bi-record-circle';
    }

    _formatEventTime(isoString) {
        const d = new Date(isoString);
        const now = new Date();
        const diff = now.getTime() - d.getTime();
        if (diff < 60000) return 'just now';
        if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
        if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
        return d.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
        });
    }
}

customElements.define('admin-view', AdminView);
export { AdminView };
